import React, { useRef, useEffect } from 'react';
import { PageElement, ElementType, Option, QuizConfig } from '../types';
import { generateId } from '../constants';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { ImageLibrary } from './ImageLibrary';

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SEAL_ICONS } from './icons/SealIcons';
import type { FormField } from '../types';

const EMOJI_CATALOG = [
  { char: '😀', keywords: ['sorriso', 'smile', 'feliz'] },
  { char: '😁', keywords: ['sorriso', 'smile', 'feliz'] },
  { char: '😂', keywords: ['risada', 'laugh', 'haha'] },
  { char: '🤣', keywords: ['risada', 'laugh', 'haha'] },
  { char: '😊', keywords: ['sorriso', 'smile', 'feliz'] },
  { char: '🙂', keywords: ['sorriso', 'smile'] },
  { char: '😉', keywords: ['piscar', 'wink'] },
  { char: '😍', keywords: ['amor', 'love', 'coração', 'coracao'] },
  { char: '😘', keywords: ['beijo', 'kiss'] },
  { char: '😗', keywords: ['beijo', 'kiss'] },
  { char: '😙', keywords: ['beijo', 'kiss'] },
  { char: '😋', keywords: ['delicia', 'gostoso', 'yum'] },
  { char: '😜', keywords: ['brincadeira', 'silly', 'lingua'] },
  { char: '🤪', keywords: ['louco', 'crazy'] },
  { char: '🤩', keywords: ['uau', 'wow', 'starstruck'] },
  { char: '😎', keywords: ['cool', 'óculos', 'oculos'] },
  { char: '🥳', keywords: ['festa', 'party', 'celebrar'] },
  { char: '🤗', keywords: ['abraço', 'hug'] },
  { char: '🤔', keywords: ['pensando', 'thinking', 'duvida'] },
  { char: '😐', keywords: ['neutro', 'neutral'] },
  { char: '😴', keywords: ['sono', 'sleep'] },
  { char: '😭', keywords: ['choro', 'cry', 'triste'] },
  { char: '😢', keywords: ['choro', 'cry', 'triste'] },
  { char: '😡', keywords: ['raiva', 'angry'] },
  { char: '😠', keywords: ['raiva', 'angry'] },
  { char: '👍', keywords: ['joinha', 'positivo', 'thumbs up', 'ok'] },
  { char: '👎', keywords: ['negativo', 'thumbs down'] },
  { char: '👌', keywords: ['ok', 'perfeito'] },
  { char: '✌️', keywords: ['paz', 'vitoria'] },
  { char: '🤞', keywords: ['dedos cruzados', 'sorte'] },
  { char: '🤟', keywords: ['amo você', 'amo voce', 'love you'] },
  { char: '🤘', keywords: ['rock', 'maloqueiro'] },
  { char: '🤙', keywords: ['shaka', 'beleza'] },
  { char: '👊', keywords: ['soquinho', 'fist bump'] },
  { char: '👏', keywords: ['aplausos', 'clap', 'palmas'] },
  { char: '🙌', keywords: ['uhul', 'celebrar'] },
  { char: '👐', keywords: ['mãos abertas', 'maos abertas'] },
  { char: '🤲', keywords: ['mãos juntas', 'maos juntas'] },
  { char: '🙏', keywords: ['oração', 'pray', 'grato', 'thanks'] },
  { char: '💪', keywords: ['força', 'musculo', 'strong'] },
  { char: '🤝', keywords: ['aperto de mãos', 'handshake', 'parceria'] },
  { char: '❤️', keywords: ['coração', 'coracao', 'love', 'amor', 'heart', 'vermelho'] },
  { char: '🧡', keywords: ['coração', 'coracao', 'love', 'amor', 'heart', 'laranja'] },
  { char: '💛', keywords: ['coração', 'coracao', 'love', 'amor', 'heart', 'amarelo'] },
  { char: '💚', keywords: ['coração', 'coracao', 'love', 'amor', 'heart', 'verde'] },
  { char: '💙', keywords: ['coração', 'coracao', 'love', 'amor', 'heart', 'azul'] },
  { char: '💜', keywords: ['coração', 'coracao', 'love', 'amor', 'heart', 'roxo'] },
  { char: '🖤', keywords: ['coração', 'coracao', 'love', 'amor', 'heart', 'preto'] },
  { char: '🤍', keywords: ['coração', 'coracao', 'love', 'amor', 'heart', 'branco'] },
  { char: '🤎', keywords: ['coração', 'coracao', 'love', 'amor', 'heart', 'marrom'] },
  { char: '💖', keywords: ['coração', 'coracao', 'love', 'amor', 'brilho', 'sparkle'] },
  { char: '💗', keywords: ['coração', 'coracao', 'love', 'amor'] },
  { char: '💓', keywords: ['coração', 'coracao', 'batendo', 'beating'] },
  { char: '💞', keywords: ['corações', 'coracoes', 'love'] },
  { char: '💕', keywords: ['corações', 'coracoes', 'love'] },
  { char: '💘', keywords: ['coração', 'coracao', 'flecha', 'arrow', 'cupido'] },
  { char: '💝', keywords: ['coração', 'coracao', 'presente', 'gift', 'love'] },
  { char: '💟', keywords: ['coração', 'coracao', 'símbolo', 'simbolo'] },
  { char: '❣️', keywords: ['exclamação', 'exclamacao', 'coração', 'coracao'] },
  { char: '💔', keywords: ['coração partido', 'coracao partido', 'broken heart'] },
  { char: '💯', keywords: ['100', 'nota', 'perfeito', 'cem'] },
  { char: '⭐', keywords: ['estrela', 'star'] },
  { char: '🌟', keywords: ['estrela', 'star', 'brilho', 'sparkle'] },
  { char: '✨', keywords: ['brilho', 'sparkles'] },
  { char: '⚡', keywords: ['raio', 'relampago', 'lightning'] },
  { char: '🔥', keywords: ['fogo', 'fire'] },
  { char: '🌈', keywords: ['arco-íris', 'arco iris', 'rainbow'] },
  { char: '☀️', keywords: ['sol', 'sun'] },
  { char: '🌙', keywords: ['lua', 'moon'] },
  { char: '🎉', keywords: ['festa', 'confete', 'party'] },
  { char: '🎊', keywords: ['festa', 'confete', 'party'] },
  { char: '🎁', keywords: ['presente', 'gift'] },
  { char: '🏆', keywords: ['troféu', 'trofeu', 'trophy'] },
  { char: '🥇', keywords: ['medalha', 'ouro', 'gold', 'primeiro'] },
  { char: '🥈', keywords: ['medalha', 'prata', 'silver'] },
  { char: '🥉', keywords: ['medalha', 'bronze'] },
  { char: '🚀', keywords: ['foguete', 'rocket', 'lancar', 'launch'] },
  { char: '💡', keywords: ['ideia', 'idea', 'lampada', 'lightbulb'] },
  { char: '👀', keywords: ['olhos', 'olhinho', 'eyes', 'ver'] },
  { char: '📣', keywords: ['megafone', 'anuncio'] },
  { char: '🔔', keywords: ['sino', 'notificacao'] },
  { char: '🔕', keywords: ['silenciar', 'mute'] },
  { char: '❓', keywords: ['duvida', 'pergunta', 'question'] },
  { char: '❗', keywords: ['exclamacao', 'importante'] },
  { char: '✅', keywords: ['ok', 'check', 'aprovado'] },
  { char: '❌', keywords: ['errado', 'x', 'cancelar'] },
  { char: '➕', keywords: ['mais', 'add'] },
  { char: '➖', keywords: ['menos', 'remove'] },
  { char: '🍎', keywords: ['maçã', 'maca', 'apple'] },
  { char: '🍊', keywords: ['laranja', 'orange'] },
  { char: '🍋', keywords: ['limao', 'limão', 'lemon'] },
  { char: '🍌', keywords: ['banana'] },
  { char: '🍉', keywords: ['melancia', 'watermelon'] },
  { char: '🍇', keywords: ['uva', 'grapes'] },
  { char: '🍓', keywords: ['morango', 'strawberry'] },
  { char: '🍒', keywords: ['cereja', 'cherry'] },
  { char: '🍍', keywords: ['abacaxi', 'pineapple'] },
  { char: '🥭', keywords: ['manga', 'mango'] },
  { char: '🥑', keywords: ['abacate', 'avocado'] },
  { char: '🍔', keywords: ['hamburguer', 'burger'] },
  { char: '🍟', keywords: ['batata frita', 'fries'] },
  { char: '🌭', keywords: ['cachorro quente', 'hot dog'] },
  { char: '🍕', keywords: ['pizza'] },
  { char: '🍣', keywords: ['sushi'] },
  { char: '🍿', keywords: ['pipoca', 'popcorn'] },
  { char: '🍫', keywords: ['chocolate'] },
  { char: '🍪', keywords: ['biscoito', 'cookie'] },
  { char: '☕', keywords: ['café', 'cafe', 'coffee'] },
  { char: '🍵', keywords: ['cha', 'chá', 'tea'] },
  { char: '🥤', keywords: ['refresco', 'drink'] },
  { char: '🍺', keywords: ['cerveja', 'beer'] },
  { char: '🍷', keywords: ['vinho', 'wine'] },
  { char: '🐶', keywords: ['cachorro', 'dog'] },
  { char: '🐱', keywords: ['gato', 'cat'] },
  { char: '🐻', keywords: ['urso', 'bear'] },
  { char: '🐼', keywords: ['panda'] },
  { char: '🐵', keywords: ['macaco', 'monkey'] },
  { char: '🐔', keywords: ['galinha', 'chicken'] },
  { char: '🐧', keywords: ['pinguim', 'penguin'] },
  { char: '🐢', keywords: ['tartaruga', 'turtle'] },
  { char: '🐍', keywords: ['cobra', 'snake'] },
  { char: '🐙', keywords: ['polvo', 'octopus'] },
  { char: '🐟', keywords: ['peixe', 'fish'] },
  { char: '⚽', keywords: ['futebol', 'soccer', 'bola'] },
  { char: '🏀', keywords: ['basquete', 'basketball'] },
  { char: '🎾', keywords: ['tênis', 'tenis', 'tennis'] },
  { char: '🏓', keywords: ['ping pong', 'mesa'] },
  { char: '🚗', keywords: ['carro', 'car'] },
  { char: '🚕', keywords: ['taxi'] },
  { char: '🚌', keywords: ['onibus', 'ônibus', 'bus'] },
  { char: '🏎️', keywords: ['carro de corrida', 'race'] },
  { char: '🚲', keywords: ['bicicleta', 'bike'] },
  { char: '✈️', keywords: ['avião', 'aviao', 'flight', 'plane'] },
  { char: '🚀', keywords: ['foguete', 'rocket'] },
  { char: '⛵', keywords: ['barco', 'sail'] },
  { char: '🏠', keywords: ['casa', 'home'] },
  { char: '🏫', keywords: ['escola', 'school'] },
  { char: '🏢', keywords: ['predio', 'prédio', 'office'] },
  { char: '🧠', keywords: ['cerebro', 'cérebro', 'brain'] },
  { char: '👀', keywords: ['olhos', 'eye'] },
  { char: '😮', keywords: ['chocado', 'surpreso', 'espantado', 'shock', 'boca aberta'] },
  { char: '😲', keywords: ['chocado', 'surpreso', 'espantado', 'astonished'] },
  { char: '😳', keywords: ['chocado', 'envergonhado', 'flushed', 'surpreso'] },
  { char: '🤯', keywords: ['chocado', 'explodido', 'exploding head', 'mind blown'] },
  { char: '🫨', keywords: ['chocado', 'tremendo', 'shaking', 'surpreso'] },
  { char: '🤭', keywords: ['surpresa', 'mão na boca', 'mao na boca', 'oops'] },
  { char: '🤫', keywords: ['silencio', 'quiet', 'shh'] },
  { char: '🤐', keywords: ['silenciado', 'zipper', 'boca fechada'] },
  { char: '🤨', keywords: ['cético', 'cetico', 'desconfiado', 'raised eyebrow'] },
  { char: '🧐', keywords: ['monóculo', 'monoculo', 'investigar'] },
  { char: '🙄', keywords: ['revirar olhos', 'rolar olhos', 'rolleyes'] },
  { char: '🙃', keywords: ['de cabeça para baixo', 'de cabeca para baixo', 'upside down'] },
  { char: '😏', keywords: ['malicioso', 'smirk', 'sarcasmo'] },
  { char: '😬', keywords: ['tenso', 'grimace', 'nervoso'] },
  { char: '😕', keywords: ['confuso', 'confused'] },
  { char: '😟', keywords: ['preocupado', 'worried'] },
  { char: '😞', keywords: ['decepcionado', 'disappointed'] },
  { char: '😔', keywords: ['pensativo', 'pensive', 'triste'] },
  { char: '😣', keywords: ['frustrado', 'desconforto', 'persevering'] },
  { char: '😖', keywords: ['angustiado', 'confuso', 'anguished'] },
  { char: '😫', keywords: ['exausto', 'tired'] },
  { char: '😩', keywords: ['cansado', 'exausto', 'weary'] },
  { char: '🥱', keywords: ['bocejo', 'yawn', 'sono'] },
  { char: '😪', keywords: ['sonolento', 'sleepy'] },
  { char: '😵', keywords: ['atordoado', 'dizzy'] },
  { char: '😵‍💫', keywords: ['tonto', 'dizzy', 'espiral'] },
  { char: '🤒', keywords: ['doente', 'termometro', 'sick'] },
  { char: '🤕', keywords: ['machucado', 'bandagem', 'injured'] },
  { char: '🤧', keywords: ['espirro', 'sneeze', 'resfriado'] },
  { char: '🤢', keywords: ['enjoado', 'nausea'] },
  { char: '🤮', keywords: ['vômito', 'vomito', 'vomitar'] },
  { char: '😷', keywords: ['máscara', 'mascara', 'doente'] },
  { char: '🤥', keywords: ['mentiroso', 'lying', 'nariz cresce'] },
  { char: '🥰', keywords: ['apaixonado', 'love', 'hearts'] },
  { char: '😆', keywords: ['riso', 'laughing'] },
  { char: '😃', keywords: ['sorriso', 'smile', 'feliz'] },
  { char: '😄', keywords: ['sorriso', 'smile', 'feliz'] },
  { char: '😅', keywords: ['alívio', 'alivio', 'suor', 'relief'] },
  { char: '😌', keywords: ['aliviado', 'relief', 'calmo'] },
  { char: '☺️', keywords: ['sorriso', 'smile', 'gentil'] },
  { char: '😇', keywords: ['anjinho', 'halo', 'angel'] },
  { char: '🩷', keywords: ['coração rosa', 'coracao rosa', 'pink heart'] },
  { char: '❤️‍🔥', keywords: ['coração em chamas', 'coracao em chamas', 'heart on fire'] },
  { char: '❤️‍🩹', keywords: ['coração remendado', 'coracao remendado', 'mending heart'] },
  { char: '👉', keywords: ['apontar', 'point', 'direita'] },
  { char: '👈', keywords: ['apontar', 'point', 'esquerda'] },
  { char: '👆', keywords: ['apontar', 'point', 'cima'] },
  { char: '👇', keywords: ['apontar', 'point', 'baixo'] },
  { char: '🫵', keywords: ['você', 'voce', 'apontar', 'you'] },
  { char: '✍️', keywords: ['escrever', 'write'] },
  { char: '🤌', keywords: ['italiano', 'pinched fingers', 'mão'] },
  { char: '🤦', keywords: ['facepalm', 'mão na testa', 'mao na testa'] },
  { char: '🤷', keywords: ['encolher ombros', 'shrug', 'não sei', 'nao sei'] },
  { char: '✔️', keywords: ['check', 'confirmado', 'ok'] },
  { char: '☑️', keywords: ['checkbox', 'check', 'ok'] },
  { char: '❎', keywords: ['x', 'negativo', 'cross mark'] },
  { char: '⚠️', keywords: ['aviso', 'warning', 'cuidado'] },
  { char: '♻️', keywords: ['reciclar', 'recycle'] },
  { char: '💸', keywords: ['dinheiro voando', 'money'] },
  { char: '💰', keywords: ['dinheiro', 'money', 'bolsa'] },
];

const ImageInput: React.FC<{
  label?: string;
  value?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onOpenLibrary?: (cb: (url: string) => void) => void;
  className?: string;
}> = ({ label, value, onChange, onOpenLibrary, className, placeholder }) => {
  return (
    <div className={className}>
      {label && <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>}
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          className="flex-1 px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
          placeholder={placeholder || "https://..."}
        />
        {onOpenLibrary && (
          <button
            type="button"
            onClick={() => onOpenLibrary((url) => onChange(url))}
            className="px-3 py-2 bg-slate-100 border border-slate-300 rounded-md hover:bg-slate-200 text-slate-600"
            title="Selecionar imagem da biblioteca"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
            </svg>
          </button>
        )}
      </div>
      {value && (
        <div className="mt-2 relative group w-20 h-20 bg-slate-100 rounded border overflow-hidden">
          <img src={value} alt="Preview" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-0 right-0 p-1 bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            title="Remover"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

interface ElementEditorProps {
  element: PageElement;
  onUpdate: (element: PageElement) => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  config?: QuizConfig;
}

const SortableOption: React.FC<{
  option: Option;
  index: number;
  onUpdate: (index: number, text: string) => void;
  onDelete: (index: number) => void;
  onUpdateMediaType: (index: number, mediaType: 'none' | 'emoji' | 'image') => void;
  onUpdateEmoji: (index: number, emoji: string) => void;
  onUpdateImageUrl: (index: number, url: string) => void;
  onOpenLibrary?: (cb: (url: string) => void) => void;
}> = ({ option, index, onUpdate, onDelete, onUpdateMediaType, onUpdateEmoji, onUpdateImageUrl, onOpenLibrary }) => {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [emojiQuery, setEmojiQuery] = React.useState('');
  const pickerRef = React.useRef<HTMLDivElement | null>(null);
  const pickerButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const normalizeEmoji = (s: string) => (s || '').replace(/\uFE0F/g, '').trim();
  const normalizeText = (s: string) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const inferredMediaType = option.mediaType || (option.imageUrl ? 'image' : option.emoji ? 'emoji' : 'none');
  const [localMediaType, setLocalMediaType] = React.useState<'none' | 'emoji' | 'image'>(inferredMediaType);

  // Only update when option.id changes (new option loaded) or when mediaType is explicitly set
  React.useEffect(() => {
    const newInferred = option.mediaType || (option.imageUrl ? 'image' : option.emoji ? 'emoji' : 'none');
    setLocalMediaType(newInferred);
  }, [option.id, option.mediaType || 'none']);
  const categoryOrder = ['faces', 'hands', 'hearts', 'nature', 'animals', 'food', 'sports', 'travel', 'objects', 'places', 'symbols', 'misc'];
  const getCategory = (item: { char: string; keywords: string[] }) => {
    const kw = item.keywords.map(normalizeText);
    const has = (terms: string[]) => terms.some(t => kw.includes(t));
    if (has(['sorriso', 'feliz', 'risada', 'choro', 'triste', 'raiva', 'surpreso', 'chocado', 'shock', 'pensativo', 'thinking', 'duvida', 'angel', 'anjinho', 'halo', 'uau', 'wow', 'tenso', 'grimace', 'nervoso', 'alivio', 'aliviado', 'calmo', 'gentil', 'sono', 'sleep', 'bocejo', 'yawn', 'dizzy', 'tonto', 'doente', 'sick', 'termometro', 'machucado', 'injured', 'resfriado', 'nausea', 'vomito', 'vômito', 'mascara', 'silenciado', 'zipper', 'mentiroso', 'lying', 'face'])) return 'faces';
    if (has(['apontar', 'joinha', 'thumbs', 'aplausos', 'palmas', 'oração', 'pray', 'mãos', 'maos', 'mão', 'mao', 'dedos', 'shaka', 'soquinho', 'escrever', 'facepalm', 'shrug'])) return 'hands';
    if (has(['coração', 'coracao', 'heart', 'amor', 'love'])) return 'hearts';
    if (has(['estrela', 'star', 'brilho', 'sparkle', 'raio', 'lightning', 'fogo', 'fire', 'arco iris', 'arco-iris', 'sun', 'sol', 'lua', 'moon', 'rainbow'])) return 'nature';
    if (has(['dog', 'cachorro', 'cat', 'gato', 'urso', 'panda', 'macaco', 'monkey', 'galinha', 'chicken', 'pinguim', 'penguin', 'tartaruga', 'turtle', 'cobra', 'snake', 'polvo', 'octopus', 'peixe', 'fish', 'animal'])) return 'animals';
    if (has(['pizza', 'sushi', 'hamburguer', 'burger', 'maçã', 'maca', 'apple', 'laranja', 'orange', 'limao', 'limão', 'lemon', 'banana', 'melancia', 'watermelon', 'uva', 'grapes', 'morango', 'strawberry', 'cereja', 'cherry', 'abacaxi', 'pineapple', 'manga', 'mango', 'abacate', 'avocado', 'pipoca', 'popcorn', 'chocolate', 'biscoito', 'cookie', 'chá', 'cha', 'tea', 'café', 'cafe', 'coffee', 'refresco', 'drink', 'cerveja', 'beer', 'vinho', 'wine'])) return 'food';
    if (has(['futebol', 'soccer', 'basquete', 'basketball', 'tênis', 'tenis', 'tennis', 'ping pong', 'mesa', 'bola'])) return 'sports';
    if (has(['carro', 'car', 'taxi', 'onibus', 'ônibus', 'bus', 'carro de corrida', 'race', 'bicicleta', 'bike', 'avião', 'aviao', 'plane', 'flight', 'rocket', 'foguete', 'barco', 'sail', 'sailboat'])) return 'travel';
    if (has(['ideia', 'idea', 'lampada', 'lightbulb', 'megafone', 'anuncio', 'sino', 'notificacao', 'mute', 'dinheiro', 'money', 'bolsa'])) return 'objects';
    if (has(['casa', 'home', 'escola', 'school', 'predio', 'prédio', 'office'])) return 'places';
    if (has(['check', 'ok', 'checkbox', 'warning', 'aviso', 'x', 'negativo', 'exclamacao', 'exclamação', 'question', 'duvida', 'pergunta', 'recycle', 'reciclar', 'mais', 'menos', 'add', 'remove'])) return 'symbols';
    return 'misc';
  };
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!pickerOpen) return;
      const target = e.target as Node;
      if (pickerRef.current && !pickerRef.current.contains(target) && pickerButtonRef.current && !pickerButtonRef.current.contains(target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-1 mb-2"
    >
      <div className="flex items-center">
        <button
          {...attributes}
          {...listeners}
          className="p-1 text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing"
          title="Arrastar para reordenar"
        >
          ⋮⋮
        </button>
      </div>
      <div className="flex-1">
        <input
          type="text"
          value={option.text}
          onChange={e => onUpdate(index, e.target.value)}
          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
          placeholder={`Opção ${index + 1}`}
        />
        <div className="mt-1 grid grid-cols-3 gap-1">
          <div className={`relative z-50 ${localMediaType === 'none' ? 'col-span-3' : ''}`}>
            <select
              value={localMediaType}
              onChange={e => {
                const mt = e.target.value as any;
                setLocalMediaType(mt);
                onUpdateMediaType(index, mt);
                if (mt !== 'emoji') setPickerOpen(false);
              }}
              onFocus={() => setPickerOpen(false)}
              onMouseDown={() => setPickerOpen(false)}
              onPointerDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
            >
              <option value="none">Sem mídia</option>
              <option value="emoji">Emoji</option>
              <option value="image">Imagem</option>
            </select>
          </div>
          {localMediaType === 'emoji' && (
            <div className="col-span-2">
              <div className="relative">
                <input
                  type="text"
                  value={option.emoji || ''}
                  onChange={e => onUpdateEmoji(index, e.target.value)}
                  className="w-full pr-8 px-2 py-1 border border-slate-300 rounded text-xs"
                  placeholder="Emoji"
                />
                <button
                  type="button"
                  onClick={() => setPickerOpen(!pickerOpen)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 px-2 py-1 text-slate-600 hover:text-slate-800"
                  ref={pickerButtonRef}
                >
                  👀
                </button>
                {pickerOpen && (
                  <div ref={pickerRef} className="absolute left-0 top-full z-[1000] mt-2 w-64 max-w-[calc(100%-0.5rem)] bg-white border border-slate-300 rounded shadow">
                    <div className="p-2">
                      <input
                        type="text"
                        value={emojiQuery}
                        onChange={e => setEmojiQuery(e.target.value)}
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
                        placeholder="Buscar emoji"
                      />
                    </div>
                    <div className="p-2 grid grid-cols-8 gap-2 max-h-56 overflow-y-auto">
                      {EMOJI_CATALOG.filter(item => {
                        const q = normalizeText(emojiQuery);
                        if (!q) return true;
                        return normalizeEmoji(item.char).includes(emojiQuery) || item.keywords.some(k => normalizeText(k).includes(q));
                      }).sort((a, b) => categoryOrder.indexOf(getCategory(a)) - categoryOrder.indexOf(getCategory(b))).map((item, i) => (
                        <button
                          key={`${item.char}-${i}`}
                          type="button"
                          onClick={() => { onUpdateEmoji(index, item.char); setPickerOpen(false); }}
                          className="hover:bg-slate-100 rounded"
                          style={{ fontSize: '1.6rem', lineHeight: '1.9rem', padding: '0.2rem' }}
                        >{item.char}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {localMediaType === 'image' && (
            <div className="col-span-2">
              <ImageInput
                value={option.imageUrl || ''}
                onChange={val => onUpdateImageUrl(index, val)}
                onOpenLibrary={onOpenLibrary}
                placeholder="https://imagem.jpg"
              />
            </div>
          )}
        </div>
      </div>
      <button
        onClick={() => onDelete(index)}
        className="p-1 text-red-500 hover:text-red-700"
        title="Remover opção"
      >
        ×
      </button>
    </div>
  );
};

interface ListSortableItemProps {
  id: string;
  index: number;
  item: any;
  onUpdate: (idx: number, patch: any) => void;
  onDelete: (idx: number) => void;
  defaultTextColor: string;
}

const ListSortableItem: React.FC<ListSortableItemProps> = ({ id, index, item, onUpdate, onDelete, defaultTextColor }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="p-3 border border-slate-200 rounded-md bg-white">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <button {...attributes} {...listeners} className="p-1 text-slate-400 hover:text-slate-600 cursor-grab" type="button" title="Arrastar para reordenar">=</button>
          <span className="text-sm font-medium text-slate-700">Item {index + 1}</span>
        </div>
        <button type="button" className="p-1 text-red-500 hover:text-red-700" title="Remover item" aria-label="Remover item" onClick={() => onDelete(index)}>×</button>
      </div>
      <input
        type="text"
        value={item.title || ''}
        onChange={e => onUpdate(index, { title: e.target.value })}
        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
        placeholder="Título do item (suporta HTML e emojis)"
      />
      <textarea
        value={item.description || ''}
        onChange={e => onUpdate(index, { description: e.target.value })}
        className="mt-2 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
        rows={3}
        placeholder="Descrição do item (suporta HTML, quebras de linha e emojis)"
      />
      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700">Fundo do item</label>
          <input
            type="color"
            value={item.bgColor || '#ffffff'}
            onChange={e => onUpdate(index, { bgColor: e.target.value })}
            className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Cor do texto</label>
          <input
            type="color"
            value={item.textColor || defaultTextColor}
            onChange={e => onUpdate(index, { textColor: e.target.value })}
            className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
          />
        </div>
      </div>
    </div>
  );
};

const FbCommentSortableItem: React.FC<{
  id: string;
  index: number;
  comment: any;
  onUpdate: (idx: number, patch: any) => void;
  onDelete: (idx: number) => void;
}> = ({ id, index, comment, onUpdate, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 2 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="p-3 bg-slate-50 rounded-md border border-slate-200 space-y-3 relative mb-3">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <button
            {...attributes}
            {...listeners}
            className="p-1 text-slate-400 hover:text-slate-600 cursor-grab px-2"
            title="Arrastar para reordenar"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
          </button>
          <span className="text-xs font-bold text-slate-500">Comentário #{index + 1}</span>
        </div>
        <button
          type="button"
          onClick={() => onDelete(index)}
          className="text-red-500 hover:text-red-700 text-xs"
        >
          Remover
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-slate-600">Nome</label>
          <input
            type="text"
            value={comment.name || ''}
            onChange={e => onUpdate(index, { name: e.target.value })}
            className="mt-1 block w-full px-2 py-1 border border-slate-300 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">Tempo (ex: 5 min ago)</label>
          <input
            type="text"
            value={comment.timeAgo || ''}
            onChange={e => onUpdate(index, { timeAgo: e.target.value })}
            className="mt-1 block w-full px-2 py-1 border border-slate-300 rounded text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600">Avatar URL</label>
        <input
          type="text"
          value={comment.avatarUrl || ''}
          onChange={e => onUpdate(index, { avatarUrl: e.target.value })}
          className="mt-1 block w-full px-2 py-1 border border-slate-300 rounded text-sm"
          placeholder="https://..."
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600">Conteúdo</label>
        <textarea
          rows={2}
          value={comment.content || ''}
          onChange={e => onUpdate(index, { content: e.target.value })}
          className="mt-1 block w-full px-2 py-1 border border-slate-300 rounded text-sm"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600">Imagem do Comentário (Opcional)</label>
        <input
          type="text"
          value={comment.imageUrl || ''}
          onChange={e => onUpdate(index, { imageUrl: e.target.value })}
          className="mt-1 block w-full px-2 py-1 border border-slate-300 rounded text-sm"
          placeholder="https://..."
        />
      </div>
    </div>
  );
};

const ElementEditor: React.FC<ElementEditorProps> = ({
  element,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  config
}) => {
  const redirectUrlRef = useRef<HTMLInputElement>(null);
  const [imgLib, setImgLib] = React.useState<{ isOpen: boolean; callback?: (url: string) => void }>({ isOpen: false });
  const openImageLib = (cb: (url: string) => void) => setImgLib({ isOpen: true, callback: cb });


  console.log('ElementEditor rendering for type:', element.type, 'ID:', element.id);
  const updateElement = (updates: Partial<PageElement>) => {
    onUpdate({ ...element, ...updates });
  };
  const [styleOpen, setStyleOpen] = React.useState(false);
  const getDefaultTextColor = (styleVal: any) => {
    return (styleVal === 'highlight' || styleVal === 'relief') ? '#ffffff' : (config?.colors.text || '#374151');
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id && element.type === ElementType.Options) {
      const options = (element as any).options as Option[];
      const oldIndex = options.findIndex((opt: Option) => opt.id === active.id);
      const newIndex = options.findIndex((opt: Option) => opt.id === over?.id);

      const newOptions = arrayMove(options, oldIndex, newIndex);
      updateElement({ options: newOptions });
    }
  };

  const renderElementContent = () => {
    switch (element.type) {
      case ElementType.Roulette:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Título</label>
              <input
                type="text"
                value={(element as any).title || ''}
                onChange={e => updateElement({ title: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Texto do Botão</label>
              <input
                type="text"
                value={(element as any).buttonText || ''}
                onChange={e => updateElement({ buttonText: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Mensagem de Vitória</label>
              <input
                type="text"
                value={(element as any).winningMessage || ''}
                onChange={e => updateElement({ winningMessage: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Ex: Você ganhou: {prize}"
              />
              <p className="mt-1 text-xs text-slate-500">Use {'{prize}'} para exibir o nome do prêmio ganho.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Subtítulo de Vitória</label>
              <input
                type="text"
                value={(element as any).subMessage || ''}
                onChange={e => updateElement({ subMessage: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Esquema de Cores</label>
              <select
                value={(element as any).colorScheme || 'grayscale'}
                onChange={e => updateElement({ colorScheme: e.target.value as any } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="grayscale">Tons de Cinza (Padrão)</option>
                <option value="colorful">Colorido (Pastel)</option>
              </select>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-slate-700">Itens da Roleta</label>
                {((element as any).items || []).length < 6 && (
                  <button
                    type="button"
                    onClick={() => {
                      const items = (element as any).items || [];
                      updateElement({
                        items: [...items, { id: generateId(), emoji: '🎁', label: 'Prêmio' }]
                      } as any);
                    }}
                    className="text-xs text-sky-600 hover:text-sky-800 font-medium"
                  >
                    + Adicionar Item
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {((element as any).items || []).map((item: any, idx: number) => (
                  <div key={item.id} className="flex items-center gap-2 p-2 border border-slate-200 rounded-md bg-slate-50">
                    <div className="w-10">
                      <input
                        type="text"
                        value={item.emoji}
                        onChange={e => {
                          const newItems = [...((element as any).items || [])];
                          newItems[idx] = { ...newItems[idx], emoji: e.target.value };
                          updateElement({ items: newItems } as any);
                        }}
                        className="w-full px-1 py-1 border border-slate-300 rounded text-center"
                        placeholder="Emoji"
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={item.label}
                        onChange={e => {
                          const newItems = [...((element as any).items || [])];
                          newItems[idx] = { ...newItems[idx], label: e.target.value };
                          updateElement({ items: newItems } as any);
                        }}
                        className="w-full px-2 py-1 border border-slate-300 rounded"
                        placeholder="Nome do prêmio"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newItems = ((element as any).items || []).filter((_: any, i: number) => i !== idx);
                        updateElement({ items: newItems } as any);
                      }}
                      className="text-red-500 hover:text-red-700 px-2"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case ElementType.SlotMachine:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Título</label>
              <input
                type="text"
                value={(element as any).title || ''}
                onChange={e => updateElement({ title: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Mensagem de Vitória</label>
              <input
                type="text"
                value={(element as any).winningMessage || ''}
                onChange={e => updateElement({ winningMessage: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Ex: PARABÉNS! VOCÊ GANHOU!"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Subtítulo de Vitória</label>
              <input
                type="text"
                value={(element as any).subMessage || ''}
                onChange={e => updateElement({ subMessage: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Ex: Clique abaixo para resgatar!"
              />
            </div>


            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={(element as any).alwaysWin !== false}
                onChange={e => updateElement({ alwaysWin: e.target.checked } as any)}
                id="alwaysWin"
              />
              <label htmlFor="alwaysWin" className="text-sm font-medium text-slate-700">Sempre Ganhar?</label>
            </div>

            {!(element as any).alwaysWin && (
              <div>
                <label className="block text-sm font-medium text-slate-700">Mensagem de Fracasso</label>
                <input
                  type="text"
                  value={(element as any).losingMessage || ''}
                  onChange={e => updateElement({ losingMessage: e.target.value } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  placeholder="Ex: Que pena, tente novamente!"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700">Máximo de Tentativas (Max 5)</label>
              <input
                type="number"
                min={1}
                max={5}
                value={(element as any).maxAttempts || 3}
                onChange={e => updateElement({ maxAttempts: Math.min(5, Math.max(1, parseInt(e.target.value) || 1)) } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>


          </div>
        );
      case ElementType.Form:
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">ID</label>
                <input type="text" value={(element as any).fieldId || ''} onChange={e => updateElement({ fieldId: e.target.value } as any)} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm" placeholder="Identificador único do campo" />
                <p className="mt-1.5 text-xs text-slate-500">
                  Use <code className="bg-slate-100 px-1 py-0.5 rounded text-sky-600">{'{id}'}</code> para exibir o valor em outras páginas.<br />
                  Transformações: <code className="bg-slate-100 px-1 py-0.5 rounded text-sky-600">{'{uppercase:id}'}</code>, <code className="bg-slate-100 px-1 py-0.5 rounded text-sky-600">{'{lowercase:id}'}</code>, <code className="bg-slate-100 px-1 py-0.5 rounded text-sky-600">{'{capitalize:id}'}</code>
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Tipo</label>
                <select value={(element as any).fieldType || 'text'} onChange={e => updateElement({ fieldType: e.target.value as any } as any)} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm">
                  <option value="text">Texto</option>
                  <option value="email">E-mail</option>
                  <option value="number">Número</option>
                  <option value="date">Data</option>
                  <option value="phone">Celular</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Label</label>
                <input type="text" value={(element as any).title || ''} onChange={e => updateElement({ title: e.target.value } as any)} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm" placeholder="" />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Tamanho da Fonte</label>
                <select value={(element as any).fontSize || 'normal'} onChange={e => updateElement({ fontSize: e.target.value as any } as any)} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm">
                  <option value="normal">Normal</option>
                  <option value="large">Grande</option>
                  <option value="extra">Extra</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Estilo</label>
                <select value={(element as any).style || 'boxed'} onChange={e => updateElement({ style: e.target.value as any } as any)} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm">
                  <option value="boxed">Boxed (Padrão)</option>
                  <option value="flat">Flat (Simples)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Exibição do Label</label>
                <select value={(element as any).labelPosition || 'tradicional'} onChange={e => updateElement({ labelPosition: e.target.value as any } as any)} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm">
                  <option value="tradicional">Tradicional</option>
                  <option value="none">Não Exibir</option>
                  <option value="floating">Flutuante</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 mt-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Placeholder</label>
                <input type="text" value={(element as any).placeholder || ''} onChange={e => updateElement({ placeholder: e.target.value } as any)} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm" placeholder="" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Máscara</label>
                <input type="text" value={(element as any).mask || ''} onChange={e => updateElement({ mask: e.target.value } as any)} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm" placeholder="Ex.: (99) 99999-9999" />
                <p className="mt-1 text-xs text-slate-500">Use 9 para dígitos. Ex.: (99) 99999-9999</p>
              </div>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" checked={(element as any).required || false} onChange={e => updateElement({ required: e.target.checked } as any)} />
                Obrigatório
              </label>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Alinhamento</label>
                <select value={(element as any).align || 'left'} onChange={e => updateElement({ align: e.target.value as any } as any)} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm">
                  <option value="left">Esquerda</option>
                  <option value="center">Centro</option>
                  <option value="right">Direita</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Borda</label>
                <select value={(element as any).border || 'small'} onChange={e => updateElement({ border: e.target.value as any } as any)} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm">
                  <option value="none">Nenhuma</option>
                  <option value="small">Pequena</option>
                  <option value="medium">Média</option>
                  <option value="large">Grande</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Sombra</label>
                <select value={(element as any).shadow || 'none'} onChange={e => updateElement({ shadow: e.target.value as any } as any)} className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm">
                  <option value="none">Nenhuma</option>
                  <option value="small">Pequena</option>
                  <option value="medium">Média</option>
                  <option value="large">Grande</option>
                </select>
              </div>
            </div>
          </div>
        );
      case ElementType.Level:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Título</label>
              <input
                type="text"
                value={(element as any).title || ''}
                onChange={e => updateElement({ title: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Subtítulo</label>
              <input
                type="text"
                value={(element as any).subtitle || ''}
                onChange={e => updateElement({ subtitle: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Porcentagem</label>
              <input
                type="number"
                min={0}
                max={100}
                value={typeof (element as any).percent === 'number' ? (element as any).percent : 0}
                onChange={e => updateElement({ percent: Math.max(0, Math.min(100, Number(e.target.value))) } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Texto do indicador</label>
              <input
                type="text"
                value={(element as any).indicatorText || ''}
                onChange={e => updateElement({ indicatorText: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Ex: Você está aqui"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Legenda Esquerda</label>
                <input
                  type="text"
                  value={(element as any).legendLeft || ''}
                  onChange={e => updateElement({ legendLeft: e.target.value } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Legenda Meio</label>
                <input
                  type="text"
                  value={(element as any).legendCenter || ''}
                  onChange={e => updateElement({ legendCenter: e.target.value } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Legenda Direita</label>
                <input
                  type="text"
                  value={(element as any).legendRight || ''}
                  onChange={e => updateElement({ legendRight: e.target.value } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Tipo</label>
              <select
                value={(element as any).styleType || 'dashes'}
                onChange={e => updateElement({ styleType: e.target.value as any } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="dashes">Traços</option>
                <option value="line">Linha</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Cor</label>
              <select
                value={(element as any).colorTheme || 'theme'}
                onChange={e => updateElement({ colorTheme: e.target.value as any } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="theme">Tema</option>
                <option value="greenToRed">Verde → Vermelho</option>
                <option value="redToGreen">Vermelho → Verde</option>
                <option value="primary">Cor primária</option>
                <option value="red">Vermelho</option>
                <option value="blue">Azul</option>
                <option value="green">Verde</option>
                <option value="yellow">Amarelo</option>
              </select>
            </div>
            <div className="flex items-center gap-6">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={(element as any).showMeter !== false}
                  onChange={e => updateElement({ showMeter: e.target.checked } as any)}
                />
                Mostrar Medidor?
              </label>
              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={(element as any).showProgress !== false}
                  onChange={e => updateElement({ showProgress: e.target.checked } as any)}
                />
                Mostrar Porcentagem?
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Borda</label>
              <select
                value={(element as any).border || 'none'}
                onChange={e => updateElement({ border: e.target.value as any } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="none">Nenhuma</option>
                <option value="small">Pequena</option>
                <option value="medium">Média</option>
                <option value="large">Grande</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Cor de fundo</label>
                <input
                  type="color"
                  value={(typeof (element as any).backgroundColor === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test((element as any).backgroundColor)) ? (element as any).backgroundColor : (config?.colors.background || '#ffffff')}
                  onChange={e => updateElement({ backgroundColor: e.target.value } as any)}
                  className="mt-1 block w-full h-10 p-1 border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Cor da barra</label>
                <input
                  type="color"
                  value={(typeof (element as any).barColor === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test((element as any).barColor)) ? (element as any).barColor : (config?.colors.primary || '#000000')}
                  onChange={e => updateElement({ barColor: e.target.value } as any)}
                  className="mt-1 block w-full h-10 p-1 border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Cor do título</label>
                <input
                  type="color"
                  value={(typeof (element as any).titleColor === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test((element as any).titleColor)) ? (element as any).titleColor : (config?.colors.title || '#1e293b')}
                  onChange={e => updateElement({ titleColor: e.target.value } as any)}
                  className="mt-1 block w-full h-10 p-1 border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Cor de texto</label>
                <input
                  type="color"
                  value={(typeof (element as any).textColor === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test((element as any).textColor)) ? (element as any).textColor : (config?.colors.text || '#64748b')}
                  onChange={e => updateElement({ textColor: e.target.value } as any)}
                  className="mt-1 block w-full h-10 p-1 border-slate-300 rounded-md"
                />
              </div>
            </div>
          </div>
        );
      case ElementType.Price:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Layout</label>
              <select
                value={(element as any).layout || 'horizontal'}
                onChange={e => updateElement({ layout: e.target.value as any } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="horizontal">Horizontal</option>
                <option value="vertical">Vertical</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Itens</label>
              <div className="space-y-2">
                {(((element as any).items || []) as any[]).map((it: any, index: number) => (
                  <div key={index} className="p-3 border border-slate-200 rounded-md bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">Item {index + 1}</span>
                      <button
                        type="button"
                        className="p-1 text-red-500 hover:text-red-700"
                        title="Remover item"
                        aria-label="Remover item"
                        onClick={() => {
                          const next = ((element as any).items || []).filter((_: any, i: number) => i !== index);
                          updateElement({ items: next } as any);
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700">Título</label>
                        <input
                          type="text"
                          value={it.title || ''}
                          onChange={e => {
                            const next = [...((element as any).items || [])];
                            next[index] = { ...next[index], title: e.target.value };
                            updateElement({ items: next } as any);
                          }}
                          className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                          placeholder="Plano PRO"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700">Cores</label>
                        <select
                          value={it.color || 'neutral'}
                          onChange={e => {
                            const next = [...((element as any).items || [])];
                            next[index] = { ...next[index], color: e.target.value };
                            updateElement({ items: next } as any);
                          }}
                          className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                        >
                          <option value="info">Informação</option>
                          <option value="warning">Aviso</option>
                          <option value="success">Sucesso</option>
                          <option value="error">Erro</option>
                          <option value="neutral">Neutro</option>
                          <option value="transparent">Transparente</option>
                          <option value="gray">Cinza</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 mt-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700">Prefixo</label>
                        <input
                          type="text"
                          value={it.prefix || ''}
                          onChange={e => {
                            const next = [...((element as any).items || [])];
                            next[index] = { ...next[index], prefix: e.target.value };
                            updateElement({ items: next } as any);
                          }}
                          className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                          placeholder="10% off"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700">Valor</label>
                        <input
                          type="text"
                          value={it.value || ''}
                          onChange={e => {
                            const next = [...((element as any).items || [])];
                            next[index] = { ...next[index], value: e.target.value };
                            updateElement({ items: next } as any);
                          }}
                          className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                          placeholder="R$ 89,90"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700">Sufixo</label>
                        <input
                          type="text"
                          value={it.suffix || ''}
                          onChange={e => {
                            const next = [...((element as any).items || [])];
                            next[index] = { ...next[index], suffix: e.target.value };
                            updateElement({ items: next } as any);
                          }}
                          className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                          placeholder="à vista"
                        />
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700">Texto Destaque Superior</label>
                        <input
                          type="text"
                          value={it.topHighlight || ''}
                          onChange={e => {
                            const next = [...((element as any).items || [])];
                            next[index] = { ...next[index], topHighlight: e.target.value };
                            updateElement({ items: next } as any);
                          }}
                          className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700">Texto Destaque Inferior</label>
                        <input
                          type="text"
                          value={it.bottomHighlight || ''}
                          onChange={e => {
                            const next = [...((element as any).items || [])];
                            next[index] = { ...next[index], bottomHighlight: e.target.value };
                            updateElement({ items: next } as any);
                          }}
                          className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700">Tipo de preço</label>
                        <select
                          value={it.priceType || 'illustrative'}
                          onChange={e => {
                            const next = [...((element as any).items || [])];
                            next[index] = { ...next[index], priceType: e.target.value };
                            updateElement({ items: next } as any);
                          }}
                          className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                        >
                          <option value="illustrative">Ilustrativo</option>
                          <option value="redirect">Redirecionar</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700">Layout do item</label>
                        <select
                          value={(element as any).layout || 'horizontal'}
                          onChange={e => updateElement({ layout: e.target.value as any } as any)}
                          className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                        >
                          <option value="horizontal">Horizontal</option>
                          <option value="vertical">Vertical</option>
                        </select>
                      </div>
                    </div>

                    {it.priceType === 'redirect' && (
                      <>
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-slate-700">Destino do redirecionamento (URL)</label>
                          <input
                            type="text"
                            value={it.url || ''}
                            onChange={e => {
                              const next = [...((element as any).items || [])];
                              next[index] = { ...next[index], url: e.target.value };
                              updateElement({ items: next } as any);
                            }}
                            className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                            placeholder="https://..."
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                          <div>
                            <label className="block text-sm font-medium text-slate-700">Onde abrir</label>
                            <select
                              value={it.target || '_blank'}
                              onChange={e => {
                                const next = [...((element as any).items || [])];
                                next[index] = { ...next[index], target: e.target.value };
                                updateElement({ items: next } as any);
                              }}
                              className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                            >
                              <option value="_self">Mesma aba</option>
                              <option value="_blank">Nova aba</option>
                            </select>
                          </div>
                          <div className="flex items-end">
                            <div className="space-y-2">
                              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                                <input
                                  type="checkbox"
                                  checked={it.enableCheckbox || false}
                                  onChange={e => {
                                    const next = [...((element as any).items || [])];
                                    next[index] = { ...next[index], enableCheckbox: e.target.checked };
                                    updateElement({ items: next } as any);
                                  }}
                                />
                                Habilitar checkbox
                              </label>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const cur = ((element as any).items || []);
                    const next = [...cur, { title: 'Novo Preço', prefix: '', value: '', suffix: '', style: 'default', priceType: 'illustrative' }];
                    updateElement({ items: next } as any);
                  }}
                  className="text-sm text-sky-600 hover:text-sky-700 font-medium"
                >
                  + Adicionar Item
                </button>
              </div>
            </div>

            <div className="bg-slate-100 rounded-lg">
              <button
                onClick={() => setStyleOpen(!styleOpen)}
                className="w-full flex justify-between items-center p-4 text-left"
                aria-expanded={styleOpen}
              >
                <h4 className="font-bold text-base text-slate-800">Estilo</h4>
                <svg
                  className={`w-5 h-5 text-slate-600 transition-transform duration-200 ${styleOpen ? 'rotate-180' : ''}`}
                  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${styleOpen ? 'max-h-none' : 'max-h-0'}`}>
                <div className="px-4 pb-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Borda</label>
                    <select
                      value={(element as any).border || 'small'}
                      onChange={e => updateElement({ border: e.target.value as any } as any)}
                      className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    >
                      <option value="none">Nenhuma</option>
                      <option value="small">Pequena</option>
                      <option value="medium">Média</option>
                      <option value="large">Grande</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Sombra</label>
                    <select
                      value={(element as any).shadow || 'none'}
                      onChange={e => updateElement({ shadow: e.target.value as any } as any)}
                      className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    >
                      <option value="none">Nenhuma</option>
                      <option value="small">Pequena</option>
                      <option value="medium">Média</option>
                      <option value="large">Grande</option>
                    </select>
                  </div>
                  {((element as any).layout || 'horizontal') === 'vertical' && (
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-slate-700">Alinhamento Vertical</label>
                      <select
                        value={(element as any).verticalAlign || 'bottom'}
                        onChange={e => updateElement({ verticalAlign: e.target.value as any } as any)}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="top">Topo</option>
                        <option value="center">Centro</option>
                        <option value="bottom">Inferior</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case ElementType.Title:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Título</label>
              <textarea
                value={element.content}
                onChange={e => updateElement({ content: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                rows={3}
                placeholder="Digite o título (suporta HTML, ex: <b>Negrito</b>, <br> para pular linha)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Alinhamento</label>
              <select
                value={(element as any).align || 'center'}
                onChange={e => updateElement({ align: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="left">Esquerda</option>
                <option value="center">Centro</option>
                <option value="right">Direita</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Tamanho da Fonte (Personalizado)</label>
              <input
                type="text"
                value={(element as any).fontSize || ''}
                onChange={e => updateElement({ fontSize: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Exemplo: 2rem ou 24px (Substitui padrão)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Altura da Linha (Line Height)</label>
              <input
                type="text"
                value={(element as any).lineHeight || ''}
                onChange={e => updateElement({ lineHeight: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Exemplo: 1.2 ou 1.5em ou 32px"
              />
            </div>
          </div>
        );

      case ElementType.Text:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Texto</label>
              <textarea
                value={element.content}
                onChange={e => updateElement({ content: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                rows={3}
                placeholder="Digite o texto..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Alinhamento</label>
              <select
                value={(element as any).align || 'center'}
                onChange={e => updateElement({ align: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="left">Esquerda</option>
                <option value="center">Centro</option>
                <option value="right">Direita</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Tamanho do Texto</label>
              <select
                value={(element as any).size || 'normal'}
                onChange={e => updateElement({ size: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="small">Small</option>
                <option value="normal">Normal</option>
                <option value="large">Large</option>
                <option value="huge">Huge</option>
              </select>
            </div>
          </div>
        );

      case ElementType.Alert:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Tipo de Alerta</label>
              <select
                value={element.variant || 'info'}
                onChange={e => updateElement({ variant: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="info">Informação</option>
                <option value="warning">Aviso</option>
                <option value="success">Sucesso</option>
                <option value="error">Erro</option>
                <option value="neutral">Neutro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Mensagem</label>
              <textarea
                value={element.content}
                onChange={e => updateElement({ content: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                rows={2}
                placeholder="Digite a mensagem do alerta..."
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={`alert-solid-${element.id}`}
                checked={(element as any).solid || false}
                onChange={e => updateElement({ solid: e.target.checked } as any)}
              />
              <label htmlFor={`alert-solid-${element.id}`} className="text-sm font-medium text-slate-700">Cores sólidas</label>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Borda</label>
              <select
                value={(element as any).borderStyle || 'solid'}
                onChange={e => updateElement({ borderStyle: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="solid">Contínua</option>
                <option value="dashed">Tracejada</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Alinhamento</label>
              <select
                value={(element as any).align || 'left'}
                onChange={e => updateElement({ align: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="left">Esquerda</option>
                <option value="center">Centro</option>
                <option value="right">Direita</option>
              </select>
            </div>
          </div>
        );

      case ElementType.Timer:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Tipo</label>
              <select
                value={(element as any).variant || 'warning'}
                onChange={e => updateElement({ variant: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="info">Informação</option>
                <option value="warning">Aviso</option>
                <option value="success">Sucesso</option>
                <option value="error">Erro</option>
                <option value="neutral">Neutro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Mensagem</label>
              <textarea
                value={(element as any).content || ''}
                onChange={e => updateElement({ content: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                rows={2}
                placeholder="Use [time] para inserir o tempo"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={`timer-solid-${element.id}`}
                checked={(element as any).solid || false}
                onChange={e => updateElement({ solid: e.target.checked } as any)}
              />
              <label htmlFor={`timer-solid-${element.id}`} className="text-sm font-medium text-slate-700">Cores sólidas</label>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Tempo (segundos)</label>
              <input
                type="number"
                min={0}
                step={1}
                value={typeof (element as any).durationSeconds === 'number' ? (element as any).durationSeconds : ''}
                onChange={e => {
                  const v = e.target.value;
                  const num = v === '' ? undefined : Math.max(0, Number(v));
                  updateElement({ durationSeconds: num } as any);
                }}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Alinhamento</label>
              <select
                value={(element as any).align || 'center'}
                onChange={e => updateElement({ align: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="left">Esquerda</option>
                <option value="center">Centro</option>
                <option value="right">Direita</option>
              </select>
            </div>
          </div>
        );

      case ElementType.Options:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Título</label>
              <textarea
                value={(element as any).title || ''}
                onChange={e => updateElement({ title: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                rows={3}
                placeholder="Digite o título da pergunta (suporta HTML, ex: <b>Negrito</b>)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Texto</label>
              <textarea
                value={(element as any).description || ''}
                onChange={e => updateElement({ description: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                rows={3}
                placeholder="Digite a descrição (suporta HTML e quebras de linha)"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={`multiple-${element.id}`}
                checked={element.multiple || false}
                onChange={e => updateElement({ multiple: e.target.checked })}
              />
              <label htmlFor={`multiple-${element.id}`} className="text-sm font-medium text-slate-700">
                Múltipla escolha
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Opções</label>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={(element as any).options?.map((opt: Option) => opt.id) || []}
                  strategy={verticalListSortingStrategy}
                >
                  {(element as any).options?.map((option: Option, index: number) => (
                    <SortableOption
                      key={option.id}
                      option={option}
                      index={index}
                      onOpenLibrary={openImageLib}
                      onUpdate={(idx, text) => {
                        const newOptions = [...(element as any).options];
                        newOptions[idx] = { ...newOptions[idx], text };
                        updateElement({ options: newOptions });
                      }}
                      onUpdateMediaType={(idx, mediaType) => {
                        const newOptions = [...(element as any).options];
                        const currentOption = newOptions[idx];
                        const updated: any = { ...currentOption, mediaType };

                        if (mediaType !== 'emoji') {
                          updated.emoji = '';
                          updated.mediaType = mediaType;
                        }

                        if (mediaType !== 'image') {
                          updated.imageUrl = '';
                        }

                        newOptions[idx] = updated;
                        updateElement({ options: newOptions });
                      }}
                      onUpdateEmoji={(idx, emoji) => {
                        const newOptions = [...(element as any).options];
                        newOptions[idx] = { ...newOptions[idx], emoji } as any;
                        updateElement({ options: newOptions });
                      }}
                      onUpdateImageUrl={(idx, url) => {
                        const newOptions = [...(element as any).options];
                        newOptions[idx] = { ...newOptions[idx], imageUrl: url };
                        updateElement({ options: newOptions });
                      }}
                      onDelete={(idx) => {
                        const newOptions = (element as any).options.filter((_: any, i: number) => i !== idx);
                        updateElement({ options: newOptions });
                      }}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <button
                onClick={() => {
                  const newOptions = [...((element as any).options || []), { id: generateId(), text: `Opção ${((element as any).options || []).length + 1}`, mediaType: 'none' }];
                  updateElement({ options: newOptions });
                }}
                className="text-sm text-sky-600 hover:text-sky-700 font-medium"
              >
                + Adicionar Opção
              </button>
            </div>
            <div className="bg-slate-100 rounded-lg">
              <div
                onClick={() => setStyleOpen(!styleOpen)}
                className="w-full flex justify-between items-center p-4 text-left cursor-pointer select-none"
                aria-expanded={styleOpen}
              >
                <h4 className="font-bold text-base text-slate-800">Estilo</h4>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm('Isso aplicará as configurações de estilo visual deste elemento a TODOS os elementos de Opções em TODAS as páginas do quiz. Tem certeza?')) {
                        window.dispatchEvent(new CustomEvent('QUIZ_APPLY_GLOBAL_STYLES', { detail: element }));
                      }
                    }}
                    className="text-xs font-semibold text-sky-600 hover:text-sky-800 hover:underline flex items-center gap-1 transition-colors px-2 py-1 rounded hover:bg-sky-50 border border-transparent hover:border-sky-100"
                    title="Aplica estilo, layout, animação e cores a todas as opções do quiz."
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copiar e Aplicar a Todos
                  </button>
                  <svg
                    className={`w-5 h-5 text-slate-600 transition-transform duration-200 ${styleOpen ? 'rotate-180' : ''}`}
                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${styleOpen ? 'max-h-none' : 'max-h-0'}`}>
                <div className="px-4 pb-4 space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Estilo</label>
                      <select
                        value={(element as any).style || 'simple'}
                        onChange={e => updateElement({ style: e.target.value as any, textColor: getDefaultTextColor(e.target.value) } as any)}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="simple">Simples</option>
                        <option value="gray">Cinza</option>
                        <option value="highlight">Destacado</option>
                        <option value="relief">Relevo</option>
                        <option value="primary">Primário</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Layout</label>
                      <select
                        value={(element as any).layout || 'grid2'}
                        onChange={e => updateElement({ layout: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="list">1 Coluna</option>
                        <option value="grid2">2 Colunas</option>
                        <option value="grid3">3 Colunas</option>
                        <option value="grid4">4 Colunas</option>
                        <option value="wrap">Itens Espalhados</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Animação</label>
                      <select
                        value={(element as any).animation || 'none'}
                        onChange={e => updateElement({ animation: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="none">Nenhuma</option>
                        <option value="border">Destacar Borda (hover)</option>
                        <option value="zoom">Zoom (hover)</option>
                        <option value="border_zoom">Borda + Zoom Suave (hover)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Orientação da Imagem/Emoji</label>
                      <select
                        value={(element as any).imageOrientation || 'horizontal'}
                        onChange={e => updateElement({ imageOrientation: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="horizontal">Horizontal</option>
                        <option value="vertical">Vertical</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Proporção de Imagens (vertical)</label>
                      <select
                        value={(element as any).verticalImageRatio || '1:1'}
                        onChange={e => updateElement({ verticalImageRatio: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="auto">Auto</option>
                        <option value="16:9">16:9 (Padrão)</option>
                        <option value="1:1">1:1 (Quadrado)</option>
                        <option value="4:3">4:3 (Retangular)</option>
                        <option value="9:16">9:16 (Mobile)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Tamanho da Imagem/Emoji</label>
                      <select
                        value={(element as any).mediaSize || 'small'}
                        onChange={e => updateElement({ mediaSize: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="small">Pequeno</option>
                        <option value="medium">Médio</option>
                        <option value="large">Grande</option>
                        <option value="extra">Extra</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Estilo do Checkbox</label>
                      <select
                        value={(element as any).checkStyle || 'traditional'}
                        onChange={e => updateElement({ checkStyle: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="none">Nenhum</option>
                        <option value="traditional">Tradicional</option>
                        <option value="square">Quadrado</option>
                        <option value="number">Numerado</option>
                        <option value="letter">Letra</option>
                        <option value="arrow">Seta</option>
                      </select>
                    </div>
                    <div style={{ display: (element as any).checkStyle === 'none' ? 'none' : 'block' }}>
                      <label className="block text-sm font-medium text-slate-700">Posição do Checkbox</label>
                      <select
                        value={(element as any).checkPosition || 'before'}
                        onChange={e => updateElement({ checkPosition: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="before">Antes do Texto</option>
                        <option value="after">Depois do Texto</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Borda</label>
                      <select
                        value={(element as any).border || 'medium'}
                        onChange={e => updateElement({ border: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="none">Nenhuma</option>
                        <option value="small">Pequena</option>
                        <option value="medium">Média</option>
                        <option value="large">Grande</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Sombra</label>
                      <select
                        value={(element as any).shadow || 'none'}
                        onChange={e => updateElement({ shadow: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="none">Nenhuma</option>
                        <option value="small">Pequena</option>
                        <option value="medium">Média</option>
                        <option value="large">Grande</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Espaçamento Interno</label>
                      <select
                        value={(element as any).spacing || 'medium'}
                        onChange={e => updateElement({ spacing: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="none">Nenhuma</option>
                        <option value="simple">Simples</option>
                        <option value="medium">Médio</option>
                        <option value="large">Grande</option>
                        <option value="extra">Extra</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Alinhamento do Texto</label>
                      <select
                        value={(element as any).textAlign || 'left'}
                        onChange={e => updateElement({ textAlign: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="left">Esquerda</option>
                        <option value="center">Centro</option>
                        <option value="right">Direita</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Peso da Fonte</label>
                      <select
                        value={(element as any).fontWeight || 'medium'}
                        onChange={e => updateElement({ fontWeight: e.target.value as any })}
                        className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      >
                        <option value="normal">Normal</option>
                        <option value="medium">Médio</option>
                        <option value="black">Black</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Cor do Texto</label>
                      <input
                        type="color"
                        value={(
                          typeof (element as any).textColor === 'string' &&
                          /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test((element as any).textColor)
                        )
                          ? (element as any).textColor
                          : (((element as any).style === 'highlight' || (element as any).style === 'relief')
                            ? '#ffffff'
                            : (config?.colors.text || '#374151'))}
                        onChange={e => updateElement({ textColor: e.target.value } as any)}
                        className="mt-1 block w-full h-10 p-1 border-slate-300 rounded-md"
                      />
                    </div>


                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case ElementType.List:
        const listItems: any[] = (element as any).items || [];
        const onDragEndList = (event: any) => {
          const { active, over } = event;
          if (!over) return;
          const oldIndex = Number(active.id);
          const newIndex = Number(over.id);
          if (Number.isNaN(oldIndex) || Number.isNaN(newIndex)) return;
          const reordered = arrayMove(listItems, oldIndex, newIndex);
          updateElement({ items: reordered } as any);
        };

        return (
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700">Posição do Título</label>
                <select
                  value={(element as any).titlePosition || 'top'}
                  onChange={e => updateElement({ titlePosition: e.target.value as any })}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="top">Em cima da descrição</option>
                  <option value="bottom">Embaixo da descrição</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700">Layout</label>
                <select
                  value={(element as any).layout || 'list'}
                  onChange={e => updateElement({ layout: e.target.value as any })}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="list">1 Coluna</option>
                  <option value="grid2">2 Colunas</option>
                  <option value="grid3">3 Colunas</option>
                  <option value="grid4">4 Colunas</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Itens</label>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndList}>
                <SortableContext items={(listItems || []).map((_: any, i: number) => String(i))} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {(listItems || []).map((it: any, index: number) => (
                      <ListSortableItem
                        key={String(index)}
                        id={String(index)}
                        index={index}
                        item={it}
                        defaultTextColor={config?.colors.text || '#374151'}
                        onUpdate={(idx, patch) => {
                          const next = [...((element as any).items || [])];
                          next[idx] = { ...next[idx], ...patch };
                          updateElement({ items: next } as any);
                        }}
                        onDelete={(idx) => {
                          const next = ((element as any).items || []).filter((_: any, ii: number) => ii !== idx);
                          updateElement({ items: next } as any);
                        }}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <button
                type="button"
                onClick={() => {
                  const items = (element as any).items || [];
                  const newItems = [...items, { title: `Novo item ${items.length + 1}`, description: 'Digite a descrição aqui...', bgColor: '#ffffff', textColor: (config?.colors.text || '#374151') }];
                  updateElement({ items: newItems });
                }}
                className="text-sm text-sky-600 hover:text-sky-700 font-medium"
              >
                + Adicionar Item
              </button>
            </div>

            <div className="bg-slate-100 rounded-lg">
              <button
                onClick={() => setStyleOpen(!styleOpen)}
                className="w-full flex justify-between items-center p-4 text-left"
                aria-expanded={styleOpen}
              >
                <h4 className="font-bold text-base text-slate-800">Estilo</h4>
                <svg
                  className={`w-5 h-5 text-slate-600 transition-transform duration-200 ${styleOpen ? 'rotate-180' : ''}`}
                  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${styleOpen ? 'max-h-none' : 'max-h-0'}`}>
                <div className="px-4 pb-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Borda</label>
                    <select
                      value={(element as any).border || 'medium'}
                      onChange={e => updateElement({ border: e.target.value as any })}
                      className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    >
                      <option value="none">Nenhuma</option>
                      <option value="small">Pequena</option>
                      <option value="medium">Média</option>
                      <option value="large">Grande</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Sombra</label>
                    <select
                      value={(element as any).shadow || 'none'}
                      onChange={e => updateElement({ shadow: e.target.value as any })}
                      className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    >
                      <option value="none">Nenhuma</option>
                      <option value="small">Pequena</option>
                      <option value="medium">Média</option>
                      <option value="large">Grande</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Espaçamento Interno</label>
                    <select
                      value={(element as any).spacing || 'medium'}
                      onChange={e => updateElement({ spacing: e.target.value as any })}
                      className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    >
                      <option value="none">Nenhuma</option>
                      <option value="simple">Simples</option>
                      <option value="medium">Médio</option>
                      <option value="large">Grande</option>
                      <option value="extra">Extra</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Alinhamento do Texto</label>
                    <select
                      value={(element as any).textAlign || 'left'}
                      onChange={e => updateElement({ textAlign: e.target.value as any })}
                      className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    >
                      <option value="left">Esquerda</option>
                      <option value="center">Centro</option>
                      <option value="right">Direita</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case ElementType.Seal:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Ícone</label>
              <div className="mt-1 grid grid-cols-8 gap-2">
                {Object.keys(SEAL_ICONS).map((name) => {
                  const Icon = (SEAL_ICONS as any)[name];
                  const active = ((element as any).icon || 'medal') === name;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => updateElement({ icon: name } as any)}
                      className={`flex items-center justify-center h-10 rounded-md border ${active ? 'border-slate-900 bg-slate-50' : 'border-slate-300 hover:border-slate-400'}`}
                      title={name}
                    >
                      <Icon className="w-6 h-6 text-slate-900" />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Cor do ícone</label>
                <input
                  type="color"
                  value={(element as any).iconColor || '#0f172a'}
                  onChange={e => updateElement({ iconColor: e.target.value } as any)}
                  className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Tamanho do ícone</label>
                <select
                  value={(element as any).iconSize || 'medium'}
                  onChange={e => updateElement({ iconSize: e.target.value as any } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="small">Pequeno</option>
                  <option value="medium">Médio</option>
                  <option value="large">Grande</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Fundo</label>
                <input
                  type="color"
                  value={(element as any).backgroundColor || '#ffffff'}
                  onChange={e => updateElement({ backgroundColor: e.target.value } as any)}
                  className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Cor do título</label>
                <input
                  type="color"
                  value={(element as any).titleColor || '#0f172a'}
                  onChange={e => updateElement({ titleColor: e.target.value } as any)}
                  className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Cor do texto</label>
                <input
                  type="color"
                  value={(element as any).textColor || '#64748b'}
                  onChange={e => updateElement({ textColor: e.target.value } as any)}
                  className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Título</label>
              <input
                type="text"
                value={(element as any).title || ''}
                onChange={e => updateElement({ title: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Seu risco é zero!"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Descrição</label>
              <textarea
                rows={3}
                value={(element as any).description || ''}
                onChange={e => updateElement({ description: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Utilize este componente para reforçar garantias, segurança e afins."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Alinhamento</label>
                <select
                  value={(element as any).align || 'center'}
                  onChange={e => updateElement({ align: e.target.value as any } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="left">Esquerda</option>
                  <option value="center">Centro</option>
                  <option value="right">Direita</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Borda</label>
                <select
                  value={(element as any).border || 'small'}
                  onChange={e => updateElement({ border: e.target.value as any } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="none">Nenhuma</option>
                  <option value="small">Pequena</option>
                  <option value="medium">Média</option>
                  <option value="large">Grande</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Sombra</label>
                <select
                  value={(element as any).shadow || 'none'}
                  onChange={e => updateElement({ shadow: e.target.value as any } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="none">Nenhuma</option>
                  <option value="small">Pequena</option>
                  <option value="medium">Média</option>
                  <option value="large">Grande</option>
                </select>
              </div>
            </div>
          </div>
        );

      case ElementType.QA:
        const qaItems: any[] = (element as any).items || [];
        const onDragEndQA = (event: any) => {
          const { active, over } = event;
          if (!over) return;
          const oldIndex = Number(active.id);
          const newIndex = Number(over.id);
          if (Number.isNaN(oldIndex) || Number.isNaN(newIndex)) return;
          const reordered = arrayMove(qaItems, oldIndex, newIndex);
          updateElement({ items: reordered } as any);
        };
        const QASortableItem: React.FC<{ id: string; index: number; item: any; onUpdate: (idx: number, patch: any) => void; onDelete: (idx: number) => void }> = ({ id, index, item, onUpdate, onDelete }) => {
          const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
          const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
          return (
            <div ref={setNodeRef} style={style} className="p-3 border border-slate-200 rounded-md bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <button {...attributes} {...listeners} className="p-1 text-slate-400 hover:text-slate-600 cursor-grab" title="Arrastar para reordenar">=</button>
                  <span className="text-sm font-medium text-slate-700">Pergunta {index + 1}</span>
                </div>
                <button
                  type="button"
                  className="p-1 text-red-500 hover:text-red-700"
                  title="Remover pergunta"
                  aria-label="Remover pergunta"
                  onClick={() => onDelete(index)}
                >
                  ×
                </button>
              </div>
              <input
                type="text"
                value={item.question || ''}
                onChange={e => onUpdate(index, { question: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Digite a pergunta..."
              />
              <textarea
                value={item.answer || ''}
                onChange={e => onUpdate(index, { answer: e.target.value })}
                className="mt-2 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                rows={3}
                placeholder="Resposta (suporta HTML e quebras de linha)"
              />
            </div>
          );
        };
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={(element as any).openFirst || false}
                  onChange={e => updateElement({ openFirst: e.target.checked })}
                />
                Primeira Q&A já aberta
              </label>
              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={(element as any).allowMultiple || false}
                  onChange={e => updateElement({ allowMultiple: e.target.checked })}
                />
                Permitir múltiplas abertas
              </label>
            </div>
            <div className="space-y-4">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndQA}>
                <SortableContext items={(qaItems || []).map((_: any, i: number) => String(i))} strategy={verticalListSortingStrategy}>
                  {(qaItems || []).map((qa: any, index: number) => (
                    <QASortableItem
                      key={String(index)}
                      id={String(index)}
                      index={index}
                      item={qa}
                      onUpdate={(idx, patch) => {
                        const newItems = [...((element as any).items || [])];
                        newItems[idx] = { ...newItems[idx], ...patch };
                        updateElement({ items: newItems });
                      }}
                      onDelete={(idx) => {
                        const newItems = (element as any).items.filter((_: any, i: number) => i !== idx);
                        updateElement({ items: newItems });
                      }}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <button
                type="button"
                onClick={() => {
                  const items = (element as any).items || [];
                  if (items.length >= 10) return;
                  const newItems = [...items, { question: `Nova pergunta ${items.length + 1}`, answer: 'Digite a resposta aqui...' }];
                  updateElement({ items: newItems });
                }}
                className="text-sm text-sky-600 hover:text-sky-700 font-medium"
              >
                + Adicionar Pergunta
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Borda</label>
                <select
                  value={(element as any).border || 'small'}
                  onChange={e => updateElement({ border: e.target.value as any })}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="none">Nenhuma</option>
                  <option value="small">Pequena</option>
                  <option value="medium">Média</option>
                  <option value="large">Grande</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Sombra</label>
                <select
                  value={(element as any).shadow || 'none'}
                  onChange={e => updateElement({ shadow: e.target.value as any })}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="none">Nenhuma</option>
                  <option value="small">Pequena</option>
                  <option value="medium">Média</option>
                  <option value="large">Grande</option>
                </select>
              </div>
            </div>
          </div>
        );

      case ElementType.Chart:
        const chartItems: any[] = (element as any).items || [];
        const onDragEndChart = (event: any) => {
          const { active, over } = event;
          if (!over) return;
          const oldIndex = Number(active.id);
          const newIndex = Number(over.id);
          if (Number.isNaN(oldIndex) || Number.isNaN(newIndex)) return;
          const reordered = arrayMove(chartItems, oldIndex, newIndex);
          updateElement({ items: reordered } as any);
        };
        const ChartSortableItem: React.FC<{ id: string; index: number; item: any; onUpdate: (idx: number, patch: any) => void; onDelete: (idx: number) => void }> = ({ id, index, item, onUpdate, onDelete }) => {
          const [dragDisabled, setDragDisabled] = React.useState(false);
          const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: dragDisabled });
          const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

          const toHex = (r: number, g: number, b: number) => '#' + [r, g, b].map(x => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')).join('');
          const hexToRgb = (hex: string) => {
            const h = (hex || '#000000').replace('#', '');
            const v = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
            const n = parseInt(v, 16);
            return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
          };
          const rgbToHsv = (r: number, g: number, b: number) => {
            r /= 255; g /= 255; b /= 255;
            const max = Math.max(r, g, b), min = Math.min(r, g, b);
            const d = max - min;
            let h = 0;
            const s = max === 0 ? 0 : d / max;
            const v = max;
            if (d !== 0) {
              switch (max) {
                case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
                case g: h = ((b - r) / d + 2); break;
                case b: h = ((r - g) / d + 4); break;
              }
              h *= 60;
            }
            return { h, s, v };
          };
          const hsvToRgb = (h: number, s: number, v: number) => {
            const c = v * s;
            const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
            const m = v - c;
            let r1 = 0, g1 = 0, b1 = 0;
            if (0 <= h && h < 60) { r1 = c; g1 = x; b1 = 0; }
            else if (60 <= h && h < 120) { r1 = x; g1 = c; b1 = 0; }
            else if (120 <= h && h < 180) { r1 = 0; g1 = c; b1 = x; }
            else if (180 <= h && h < 240) { r1 = 0; g1 = x; b1 = c; }
            else if (240 <= h && h < 300) { r1 = x; g1 = 0; b1 = c; }
            else { r1 = c; g1 = 0; b1 = x; }
            return { r: Math.round((r1 + m) * 255), g: Math.round((g1 + m) * 255), b: Math.round((b1 + m) * 255) };
          };

          const ColorPicker: React.FC<{ label: string; value: string; onChange: (hex: string) => void }> = ({ label, value, onChange }) => {
            const initRgb = hexToRgb(value || '#000000');
            const initHsv = rgbToHsv(initRgb.r, initRgb.g, initRgb.b);
            const [h, setH] = React.useState<number>(initHsv.h || 0);
            const [s, setS] = React.useState<number>(initHsv.s || 0);
            const [v, setV] = React.useState<number>(initHsv.v || 0);
            const squareRef = React.useRef<HTMLDivElement | null>(null);
            const hex = toHex(hsvToRgb(h, s, v).r, hsvToRgb(h, s, v).g, hsvToRgb(h, s, v).b);
            React.useEffect(() => {
              const rgb = hexToRgb(value || '#000000');
              const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
              setH(hsv.h || 0);
              setS(hsv.s || 0);
              setV(hsv.v || 0);
            }, [value]);
            const setFromSquare = (clientX: number, clientY: number) => {
              const rect = squareRef.current?.getBoundingClientRect();
              if (!rect) return;
              const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
              const y = Math.max(0, Math.min(rect.height, clientY - rect.top));
              const ns = x / rect.width;
              const nv = 1 - y / rect.height;
              setS(ns);
              setV(nv);
              const rgb = hsvToRgb(h, ns, nv);
              onChange(toHex(rgb.r, rgb.g, rgb.b));
            };
            const onMouseDown = (e: React.MouseEvent) => {
              e.preventDefault(); e.stopPropagation(); setDragDisabled(true);
              setFromSquare(e.clientX, e.clientY);
              const onMove = (ev: MouseEvent) => { setFromSquare(ev.clientX, ev.clientY); };
              const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); setDragDisabled(false); };
              window.addEventListener('mousemove', onMove);
              window.addEventListener('mouseup', onUp);
            };
            const onTouchStart = (e: React.TouchEvent) => {
              e.preventDefault(); e.stopPropagation(); setDragDisabled(true);
              const t = e.touches[0]; setFromSquare(t.clientX, t.clientY);
              const onMove = (ev: TouchEvent) => { const tt = ev.touches[0]; if (tt) setFromSquare(tt.clientX, tt.clientY); };
              const onEnd = () => { window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onEnd); setDragDisabled(false); };
              window.addEventListener('touchmove', onMove, { passive: false });
              window.addEventListener('touchend', onEnd);
            };
            const hueChange = (val: number) => {
              setH(val);
              const rgb = hsvToRgb(val, s, v);
              onChange(toHex(rgb.r, rgb.g, rgb.b));
            };
            const onHexInput = (t: string) => {
              const rgb = hexToRgb(t);
              const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
              setH(hsv.h || 0); setS(hsv.s || 0); setV(hsv.v || 0);
              onChange(toHex(rgb.r, rgb.g, rgb.b));
            };
            const baseColor = `hsl(${h}, 100%, 50%)`;
            const indicatorX = `${s * 100}%`;
            const indicatorY = `${(1 - v) * 100}%`;
            return (
              <div className="mt-1">
                <div className="text-sm font-medium text-slate-700 mb-1">{label}</div>
                <div className="flex gap-3 items-start">
                  <div className="relative w-40 h-28 rounded-md border border-slate-300" style={{ backgroundImage: `linear-gradient(to right, #fff, ${baseColor}), linear-gradient(to top, #000, transparent)` }} ref={squareRef} onMouseDown={onMouseDown} onTouchStart={onTouchStart}>
                    <div className="absolute w-3 h-3 rounded-full border border-white shadow" style={{ left: indicatorX, top: indicatorY, transform: 'translate(-50%, -50%)', backgroundColor: hex }} />
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <input type="range" min={0} max={360} value={h} onChange={e => hueChange(Number(e.target.value))} className="w-full" onPointerDown={e => { e.stopPropagation(); setDragDisabled(true); }} onPointerUp={e => { e.stopPropagation(); setDragDisabled(false); }} />
                    <div className="flex gap-2 items-center">
                      <div className="w-8 h-8 rounded border border-slate-300" style={{ backgroundColor: hex }} />
                      <input type="text" value={hex} onChange={e => onHexInput(e.target.value)} className="flex-1 px-2 py-1 border border-slate-300 rounded-md text-sm" onPointerDown={e => { e.stopPropagation(); setDragDisabled(true); }} onPointerUp={e => { e.stopPropagation(); setDragDisabled(false); }} />
                    </div>
                  </div>
                </div>
              </div>
            );
          };
          return (
            <div ref={setNodeRef} style={style} className="p-3 border border-slate-200 rounded-md bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <button {...attributes} {...listeners} className="p-1 text-slate-400 hover:text-slate-600 cursor-grab" title="Arrastar para reordenar">=</button>
                  <span className="text-sm font-medium text-slate-700">Item {index + 1}</span>
                </div>
                <button type="button" className="p-1 text-red-500 hover:text-red-700" title="Remover item" aria-label="Remover item" onClick={() => onDelete(index)}>×</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Tipo</label>
                  <select
                    value={item.type || 'bar'}
                    onChange={e => onUpdate(index, { type: e.target.value as any })}
                    className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  >
                    <option value="bar">Barra</option>
                    <option value="donut">Donut</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Percentual</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={typeof item.percent === 'number' ? item.percent : 0}
                    onChange={e => onUpdate(index, { percent: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                    className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Cor</label>
                  <input
                    type="color"
                    defaultValue={item.color || (config?.colors.primary || '#3b82f6')}
                    onChange={e => { onUpdate(index, { color: (e.target as HTMLInputElement).value }); setDragDisabled(false); }}
                    className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                    onPointerDown={e => { e.stopPropagation(); setDragDisabled(true); }}
                    onPointerDownCapture={e => { e.stopPropagation(); setDragDisabled(true); }}
                    onClick={e => e.stopPropagation()}
                    onClickCapture={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                    onMouseDownCapture={e => e.stopPropagation()}
                    onTouchStart={e => e.stopPropagation()}
                    onTouchStartCapture={e => e.stopPropagation()}
                    onFocus={e => e.stopPropagation()}
                    onBlur={() => setDragDisabled(false)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Cor do percentual</label>
                  <input
                    type="color"
                    defaultValue={item.percentColor || '#0f172a'}
                    onChange={e => { onUpdate(index, { percentColor: (e.target as HTMLInputElement).value }); setDragDisabled(false); }}
                    className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                    onPointerDown={e => { e.stopPropagation(); setDragDisabled(true); }}
                    onPointerDownCapture={e => { e.stopPropagation(); setDragDisabled(true); }}
                    onClick={e => e.stopPropagation()}
                    onClickCapture={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                    onMouseDownCapture={e => e.stopPropagation()}
                    onTouchStart={e => e.stopPropagation()}
                    onTouchStartCapture={e => e.stopPropagation()}
                    onFocus={e => e.stopPropagation()}
                    onBlur={() => setDragDisabled(false)}
                  />
                </div>
              </div>
              <div className="mt-2">
                <label className="block text-sm font-medium text-slate-700">Título</label>
                <input
                  type="text"
                  value={item.title || ''}
                  onChange={e => onUpdate(index, { title: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  placeholder="Ex.: Nível 1"
                />
              </div>
              <div className="mt-2">
                <label className="block text-sm font-medium text-slate-700">Texto</label>
                <textarea
                  rows={3}
                  value={item.text || ''}
                  onChange={e => onUpdate(index, { text: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  placeholder="Descrição do item"
                />
              </div>
            </div>
          );
        };
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Layout</label>
                <select
                  value={(element as any).layout || 'grid2'}
                  onChange={e => updateElement({ layout: e.target.value as any } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="list">1 Coluna</option>
                  <option value="grid2">2 Colunas</option>
                  <option value="grid3">3 Colunas</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Animação (hover)</label>
                <select
                  value={(element as any).animation || 'none'}
                  onChange={e => updateElement({ animation: e.target.value as any } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="none">Nenhuma</option>
                  <option value="border">Borda</option>
                  <option value="zoom">Zoom</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Itens</label>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndChart}>
                <SortableContext items={(chartItems || []).map((_: any, i: number) => String(i))} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {(chartItems || []).map((it: any, index: number) => (
                      <ChartSortableItem
                        key={String(index)}
                        id={String(index)}
                        index={index}
                        item={it}
                        onUpdate={(idx, patch) => {
                          const next = [...((element as any).items || [])];
                          next[idx] = { ...next[idx], ...patch };
                          updateElement({ items: next } as any);
                        }}
                        onDelete={(idx) => {
                          const next = ((element as any).items || []).filter((_: any, ii: number) => ii !== idx);
                          updateElement({ items: next } as any);
                        }}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <button
                type="button"
                onClick={() => {
                  const items = (element as any).items || [];
                  const newItems = [...items, { type: 'bar', percent: 30, title: 'Novo', text: 'Digite a descrição aqui...', color: (config?.colors.primary || '#3b82f6') }];
                  updateElement({ items: newItems });
                }}
                className="text-sm text-sky-600 hover:text-sky-700 font-medium"
              >
                + Adicionar Item
              </button>
            </div>

            <div className="bg-slate-100 rounded-lg">
              <button
                onClick={() => setStyleOpen(!styleOpen)}
                className="w-full flex justify-between items-center p-4 text-left"
                aria-expanded={styleOpen}
              >
                <h4 className="font-bold text-base text-slate-800">Estilo</h4>
                <svg
                  className={`w-5 h-5 text-slate-600 transition-transform duration-200 ${styleOpen ? 'rotate-180' : ''}`}
                  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${styleOpen ? 'max-h-none' : 'max-h-0'}`}>
                <div className="px-4 pb-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Borda</label>
                    <select
                      value={(element as any).border || 'small'}
                      onChange={e => updateElement({ border: e.target.value as any })}
                      className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    >
                      <option value="none">Nenhuma</option>
                      <option value="small">Pequena</option>
                      <option value="medium">Média</option>
                      <option value="large">Grande</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Sombra</label>
                    <select
                      value={(element as any).shadow || 'none'}
                      onChange={e => updateElement({ shadow: e.target.value as any })}
                      className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    >
                      <option value="none">Nenhuma</option>
                      <option value="small">Pequena</option>
                      <option value="medium">Média</option>
                      <option value="large">Grande</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Fundo</label>
                    <input
                      type="color"
                      value={(element as any).backgroundColor || '#ffffff'}
                      onChange={e => updateElement({ backgroundColor: e.target.value } as any)}
                      className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Cor da borda</label>
                    <input
                      type="color"
                      value={(element as any).borderColor || (config?.colors.primary || '#3b82f6')}
                      onChange={e => updateElement({ borderColor: e.target.value } as any)}
                      className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Cor do texto</label>
                    <input
                      type="color"
                      value={(element as any).textColor || (config?.colors.text || '#64748b')}
                      onChange={e => updateElement({ textColor: e.target.value } as any)}
                      className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case ElementType.Report:
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Título</label>
                <input
                  type="text"
                  value={(element as any).title || ''}
                  onChange={e => updateElement({ title: e.target.value } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  placeholder="Sua pontuação"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Resultado</label>
                <input
                  type="text"
                  value={(element as any).resultLabel || ''}
                  onChange={e => updateElement({ resultLabel: e.target.value } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  placeholder="<b>Resultado:</b> Bom 😍"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Pontuação</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={typeof (element as any).score === 'number' ? (element as any).score : 0}
                  onChange={e => updateElement({ score: Math.max(0, Math.min(100, Number(e.target.value) || 0)) } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>
              <div>
                <ImageInput
                  label="URL da Imagem"
                  value={(element as any).imageUrl || ''}
                  onChange={val => updateElement({ imageUrl: val } as any)}
                  onOpenLibrary={openImageLib}
                  placeholder="https://..."
                />
              </div>
            </div>



            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Cor do gráfico</label>
                <input
                  type="color"
                  value={(element as any).scoreColor || (config?.colors.primary || '#3b82f6')}
                  onChange={e => updateElement({ scoreColor: (e.target as HTMLInputElement).value } as any)}
                  className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Cor do percentual</label>
                <input
                  type="color"
                  value={(element as any).percentColor || '#0f172a'}
                  onChange={e => updateElement({ percentColor: (e.target as HTMLInputElement).value } as any)}
                  className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Cor da borda</label>
                <input
                  type="color"
                  value={(element as any).borderColor || '#cbd5e1'}
                  onChange={e => updateElement({ borderColor: e.target.value } as any)}
                  className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Cor do título</label>
                <input
                  type="color"
                  value={(element as any).titleColor || (config?.colors.text || '#0f172a')}
                  onChange={e => updateElement({ titleColor: e.target.value } as any)}
                  className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                />
              </div>
            </div>
          </div>
        );

      case ElementType.Image:
        return (
          <div className="space-y-3">
            <div>
              <ImageInput
                label="URL da Imagem"
                value={element.url}
                onChange={val => updateElement({ url: val })}
                onOpenLibrary={openImageLib}
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Texto Alternativo</label>
              <input
                type="text"
                value={element.alt || ''}
                onChange={e => updateElement({ alt: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Descrição da imagem..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Alinhamento</label>
              <select
                value={(element as any).align || 'center'}
                onChange={e => updateElement({ align: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="left">Esquerda</option>
                <option value="center">Centro</option>
                <option value="right">Direita</option>
              </select>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between h-6">
                <span className="text-sm font-medium text-slate-700 leading-none">Largura</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="px-2 py-0.5 text-slate-600 border border-slate-300 rounded"
                    onClick={() => updateElement({ widthPercent: Math.max(10, ((element as any).widthPercent ?? 100) - 5) })}
                  >
                    -
                  </button>
                  <span className="text-xs font-medium text-slate-600 leading-none">{((element as any).widthPercent ?? 100)}%</span>
                  <button
                    type="button"
                    className="px-2 py-0.5 text-slate-600 border border-slate-300 rounded"
                    onClick={() => updateElement({ widthPercent: Math.min(100, ((element as any).widthPercent ?? 100) + 5) })}
                  >
                    +
                  </button>
                </div>
              </div>
              <input
                type="range"
                min={10}
                max={100}
                step={1}
                value={(element as any).widthPercent ?? 100}
                onChange={e => updateElement({ widthPercent: Number(e.target.value) })}
                className="mt-2 w-full"
              />
            </div>
            <div className="mt-4 flex items-center">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={(element as any).clickToNext || false}
                  onChange={e => updateElement({ clickToNext: e.target.checked } as any)}
                  className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                Avançar ao clicar na imagem?
              </label>
            </div>
          </div>
        );

      case ElementType.BeforeAfter:
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <ImageInput
                  label="URL Antes"
                  value={(element as any).beforeUrl || ''}
                  onChange={val => updateElement({ beforeUrl: val } as any)}
                  onOpenLibrary={openImageLib}
                  placeholder="https://..."
                />
              </div>
              <div>
                <ImageInput
                  label="URL Depois"
                  value={(element as any).afterUrl || ''}
                  onChange={val => updateElement({ afterUrl: val } as any)}
                  onOpenLibrary={openImageLib}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Rótulo Antes</label>
                <input
                  type="text"
                  value={(element as any).beforeLabel || ''}
                  onChange={e => updateElement({ beforeLabel: e.target.value } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  placeholder="Antes"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Rótulo Depois</label>
                <input
                  type="text"
                  value={(element as any).afterLabel || ''}
                  onChange={e => updateElement({ afterLabel: e.target.value } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  placeholder="Depois"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Orientação</label>
              <select
                value={(element as any).orientation || 'vertical'}
                onChange={e => updateElement({ orientation: e.target.value as any } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="vertical">Vertical (esquerda/direita)</option>
                <option value="horizontal">Horizontal (cima/baixo)</option>
              </select>
            </div>
            <div className="mt-1">
              <div className="flex items-center justify-between h-6">
                <span className="text-sm font-medium text-slate-700 leading-none">Posição Inicial</span>
                <div className="flex items-center gap-2">
                  <button type="button" className="px-2 py-0.5 text-slate-600 border border-slate-300 rounded" onClick={() => updateElement({ startPercent: Math.max(0, (((element as any).startPercent ?? 50) - 5)) } as any)}>-</button>
                  <span className="text-xs font-medium text-slate-600 leading-none">{((element as any).startPercent ?? 50)}%</span>
                  <button type="button" className="px-2 py-0.5 text-slate-600 border border-slate-300 rounded" onClick={() => updateElement({ startPercent: Math.min(100, (((element as any).startPercent ?? 50) + 5)) } as any)}>+</button>
                </div>
              </div>
              <input type="range" min={0} max={100} step={1} value={(element as any).startPercent ?? 50} onChange={e => updateElement({ startPercent: Number(e.target.value) } as any)} className="mt-2 w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Alinhamento</label>
              <select
                value={(element as any).align || 'center'}
                onChange={e => updateElement({ align: e.target.value as any } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="left">Esquerda</option>
                <option value="center">Centro</option>
                <option value="right">Direita</option>
              </select>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between h-6">
                <span className="text-sm font-medium text-slate-700 leading-none">Largura</span>
                <div className="flex items-center gap-2">
                  <button type="button" className="px-2 py-0.5 text-slate-600 border border-slate-300 rounded" onClick={() => updateElement({ widthPercent: Math.max(10, (((element as any).widthPercent ?? 100) - 5)) } as any)}>-</button>
                  <span className="text-xs font-medium text-slate-600 leading-none">{((element as any).widthPercent ?? 100)}%</span>
                  <button type="button" className="px-2 py-0.5 text-slate-600 border border-slate-300 rounded" onClick={() => updateElement({ widthPercent: Math.min(100, (((element as any).widthPercent ?? 100) + 5)) } as any)}>+</button>
                </div>
              </div>
              <input type="range" min={10} max={100} step={1} value={(element as any).widthPercent ?? 100} onChange={e => updateElement({ widthPercent: Number(e.target.value) } as any)} className="mt-2 w-full" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Borda</label>
                <select
                  value={(element as any).border || 'small'}
                  onChange={e => updateElement({ border: e.target.value as any } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="none">Nenhuma</option>
                  <option value="small">Pequena</option>
                  <option value="medium">Média</option>
                  <option value="large">Grande</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Sombra</label>
                <select
                  value={(element as any).shadow || 'none'}
                  onChange={e => updateElement({ shadow: e.target.value as any } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="none">Nenhuma</option>
                  <option value="small">Pequena</option>
                  <option value="medium">Média</option>
                  <option value="large">Grande</option>
                </select>
              </div>
            </div>
          </div>
        );

      case ElementType.Audio:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">URL do Áudio</label>
              <input
                type="text"
                value={element.url}
                onChange={e => updateElement({ url: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Pergunta (opcional)</label>
              <input
                type="text"
                value={element.question || ''}
                onChange={e => updateElement({ question: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Pergunta após o áudio..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Estilo</label>
              <select
                value={(element as any).style || 'default'}
                onChange={e => updateElement({ style: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="default">Padrão</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>
            {(element as any).style === 'whatsapp' && (
              <>
                <div>
                  <ImageInput
                    label="URL do Avatar (opcional)"
                    value={(element as any).avatarUrl || ''}
                    onChange={val => updateElement({ avatarUrl: val })}
                    onOpenLibrary={openImageLib}
                    placeholder="https://..."
                  />
                </div>
                <div className="flex items-center">
                  <input
                    id="showDuration"
                    type="checkbox"
                    checked={(element as any).showDuration || false}
                    onChange={e => updateElement({ showDuration: e.target.checked })}
                    className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                  />
                  <label htmlFor="showDuration" className="ml-2 block text-sm text-slate-700">
                    Mostrar duração total
                  </label>
                </div>
              </>
            )}
          </div>
        );

      case ElementType.Spacer:
        return (
          <div>
            <label className="block text-sm font-medium text-slate-700">Altura do Espaçador (px)</label>
            <input
              type="number"
              value={element.height || 20}
              onChange={e => updateElement({ height: parseInt(e.target.value) || 20 })}
              className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              min="10"
              max="200"
            />
          </div>
        );

      case ElementType.Video:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Código de Incorporação (Embed) do Vídeo</label>
              <input
                type="text"
                value={(element as any).url || ''}
                onChange={e => {
                  const v = e.target.value;
                  if (v.includes('<iframe')) {
                    const mSrc = v.match(/src\s*=\s*["']([^"']+)["']/i);
                    const mTitle = v.match(/title\s*=\s*["']([^"']+)["']/i);
                    const mAllow = v.match(/allow\s*=\s*["']([^"']+)["']/i);
                    const mRef = v.match(/referrerpolicy\s*=\s*["']([^"']+)["']/i);
                    const hasFs = /allowfullscreen/i.test(v);

                    let extractedTitle = mTitle ? mTitle[1] : (element as any).title;
                    // Ignorar título padrão do YouTube
                    if (extractedTitle === 'YouTube video player') {
                      extractedTitle = (element as any).title || '';
                    }

                    updateElement({
                      url: (mSrc ? mSrc[1] : '').replace(/`/g, '').trim(),
                      title: extractedTitle,
                      allow: mAllow ? mAllow[1] : (element as any).allow,
                      referrerPolicy: mRef ? mRef[1] : (element as any).referrerPolicy,
                      allowFullScreen: hasFs ? true : (element as any).allowFullScreen
                    });
                  } else {
                    updateElement({ url: v.replace(/`/g, '').trim() });
                  }
                }}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="https://www.youtube.com/embed/... ou https://vimeo.com/..."
              />
              <p className="mt-1 text-xs text-slate-500">
                Use URLs embed do YouTube, Vimeo ou outras plataformas suportadas
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Título (opcional)</label>
              <input
                type="text"
                value={(element as any).title || ''}
                onChange={e => updateElement({ title: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Título do vídeo"
              />
            </div>
            <div className="flex items-center">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={(element as any).autoplay || false}
                  onChange={e => updateElement({ autoplay: e.target.checked } as any)}
                  className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                Autoplay?
              </label>
            </div>
            <div className="flex items-center">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={(element as any).cleanPlayer || false}
                  onChange={e => updateElement({ cleanPlayer: e.target.checked } as any)}
                  className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                Player Limpo (YouTube)?
              </label>
            </div>
            {(element as any).cleanPlayer && (
              <div className="flex items-center pl-6">
                <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-600">
                  <input
                    type="checkbox"
                    checked={(element as any).hideInfo || false}
                    onChange={e => updateElement({ hideInfo: e.target.checked } as any)}
                    className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  />
                  Ocultar Título/Avatar (Corta Topo)
                </label>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700">Proporção</label>
              <select
                value={(element as any).aspectRatio || '16:9'}
                onChange={e => updateElement({ aspectRatio: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="16:9">16:9 (Horizontal)</option>
                <option value="9:16">9:16 (Vertical / Reels / Shorts)</option>
                <option value="4:3">4:3</option>
                <option value="1:1">1:1 (Quadrado)</option>
              </select>
            </div>
          </div>
        );

      case ElementType.Carousel:
        const items: any[] = (element as any).items || [];
        const onDragEndItems = (event: any) => {
          const { active, over } = event;
          if (!over) return;
          const oldIndex = items.findIndex((it: any, i: number) => (it.id || i) === active.id);
          const newIndex = items.findIndex((it: any, i: number) => (it.id || i) === over.id);
          if (oldIndex < 0 || newIndex < 0) return;
          const reordered = [...items];
          const [moved] = reordered.splice(oldIndex, 1);
          reordered.splice(newIndex, 0, moved);
          updateElement({ items: reordered } as any);
        };
        const CarouselSortableItem: React.FC<{ id: string; index: number; item: any; onUpdate: (idx: number, patch: any) => void; onDelete: (idx: number) => void; onOpenLibrary: (cb: (url: string) => void) => void }> = ({ id, index, item, onUpdate, onDelete, onOpenLibrary }) => {
          const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
          const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
          const [localDesc, setLocalDesc] = React.useState<string>(item.description || '');
          React.useEffect(() => { setLocalDesc(item.description || ''); }, [item.description]);
          return (
            <div ref={setNodeRef} style={style} className="p-3 border border-slate-200 rounded-md bg-white mb-2">
              <div className="flex items-start gap-2">
                <button
                  {...attributes}
                  {...listeners}
                  className="p-1 text-slate-400 hover:text-slate-600 cursor-grab self-center"
                  title="Arrastar para reordenar"
                >
                  =
                </button>
                <div className="flex-1">
                  <ImageInput
                    label="Imagem (URL)"
                    value={item.imageUrl || ''}
                    onChange={val => onUpdate(index, { imageUrl: val })}
                    onOpenLibrary={onOpenLibrary}
                    placeholder="https://..."
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700">Descrição</label>
                  <input
                    type="text"
                    value={localDesc}
                    onChange={e => setLocalDesc(e.target.value)}
                    onBlur={() => onUpdate(index, { description: localDesc })}
                    onKeyDown={e => { if (e.key === 'Enter') { onUpdate(index, { description: localDesc }); } }}
                    className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    placeholder="Exemplo de descrição"
                  />
                </div>
                <button
                  type="button"
                  className="p-1 text-red-500 hover:text-red-700 self-center"
                  title="Remover item"
                  onClick={() => onDelete(index)}
                >
                  ×
                </button>
              </div>
            </div>
          );
        };
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Layout</label>
              <select
                value={(element as any).layout || 'image-text'}
                onChange={e => updateElement({ layout: e.target.value as any } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="image-text">Imagem e Texto</option>
                <option value="image">Somente Imagem</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Paginação</label>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={(element as any).pagination !== false} onChange={e => updateElement({ pagination: e.target.checked } as any)} id={`carousel-pagination-${element.id}`} />
                  <label htmlFor={`carousel-pagination-${element.id}`} className="text-sm text-slate-700">Exibir bolinhas</label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Autoplay</label>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={(element as any).autoplay || false} onChange={e => updateElement({ autoplay: e.target.checked } as any)} id={`carousel-autoplay-${element.id}`} />
                  <label htmlFor={`carousel-autoplay-${element.id}`} className="text-sm text-slate-700">Alternar automaticamente</label>
                </div>
                <div className="mt-2" style={{ display: (element as any).autoplay ? 'block' : 'none' }}>
                  <label className="block text-xs font-medium text-slate-700">Intervalo (ms)</label>
                  <input type="number" min={500} step={100} value={(element as any).autoplayDelayMs ?? 3000} onChange={e => updateElement({ autoplayDelayMs: Number(e.target.value) } as any)} className="mt-1 block w-full px-2 py-1 border border-slate-300 rounded-md text-xs" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Alinhamento</label>
                <select
                  value={(element as any).align || 'center'}
                  onChange={e => updateElement({ align: e.target.value as any } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="left">Esquerda</option>
                  <option value="center">Centro</option>
                  <option value="right">Direita</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Proporção</label>
                <select
                  value={(element as any).ratio || 'auto'}
                  onChange={e => updateElement({ ratio: e.target.value as any } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="auto">Auto</option>
                  <option value="16:9">16:9</option>
                  <option value="1:1">1:1</option>
                  <option value="4:3">4:3</option>
                  <option value="9:16">9:16</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between h-6">
                <span className="text-sm font-medium text-slate-700 leading-none">Largura</span>
                <div className="flex items-center gap-2">
                  <button type="button" className="px-2 py-0.5 text-slate-600 border border-slate-300 rounded" onClick={() => updateElement({ widthPercent: Math.max(10, ((element as any).widthPercent ?? 100) - 5) } as any)}>-</button>
                  <span className="text-xs font-medium text-slate-600 leading-none">{((element as any).widthPercent ?? 100)}%</span>
                  <button type="button" className="px-2 py-0.5 text-slate-600 border border-slate-300 rounded" onClick={() => updateElement({ widthPercent: Math.min(100, ((element as any).widthPercent ?? 100) + 5) } as any)}>+</button>
                </div>
              </div>
              <input type="range" min={10} max={100} step={1} value={(element as any).widthPercent ?? 100} onChange={e => updateElement({ widthPercent: Number(e.target.value) } as any)} className="mt-2 w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Itens</label>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndItems}>
                <SortableContext items={(items || []).map((it: any, i: number) => (it.id || String(i)))} strategy={verticalListSortingStrategy}>
                  {(items || []).map((it: any, i: number) => (
                    <CarouselSortableItem
                      key={it.id || String(i)}
                      id={it.id || String(i)}
                      index={i}
                      item={it}
                      onOpenLibrary={openImageLib}
                      onUpdate={(idx, patch) => {
                        const next = [...((element as any).items || [])];
                        next[idx] = { ...next[idx], ...patch };
                        updateElement({ items: next } as any);
                      }}
                      onDelete={(idx) => {
                        const next = ((element as any).items || []).filter((_: any, ii: number) => ii !== idx);
                        updateElement({ items: next } as any);
                      }}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <button
                onClick={() => {
                  const cur = ((element as any).items || []);
                  const next = [...cur, { id: generateId(), imageUrl: '', description: 'Exemplo de descrição' }];
                  updateElement({ items: next } as any);
                }}
                className="text-sm text-sky-600 hover:text-sky-700 font-medium"
              >
                + adicionar
              </button>
            </div>
          </div>
        );

      case ElementType.Button:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Texto do Botão</label>
              <input
                type="text"
                value={(element as any).text || 'Continuar'}
                onChange={e => updateElement({ text: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Texto do botão..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Alinhamento</label>
              <select
                value={(element as any).align || 'center'}
                onChange={e => updateElement({ align: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="left">Esquerda</option>
                <option value="center">Centro</option>
                <option value="right">Direita</option>
              </select>
            </div>
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Cor</label>
                <input
                  type="color"
                  value={(element as any).color ?? (config?.colors.primary || '#000000')}
                  onChange={e => updateElement({ color: e.target.value } as any)}
                  className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Estilo</label>
                <select
                  value={(element as any).styleVariant || 'normal'}
                  onChange={e => updateElement({ styleVariant: e.target.value as any } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="normal">Normal</option>
                  <option value="alto">Alto Relevo</option>
                  <option value="sombra">Sombra</option>
                </select>
              </div>
            </div>
            <div className="mt-2">
              <label className="block text-sm font-medium text-slate-700">Altura</label>
              <select
                value={(element as any).height || 'medium'}
                onChange={e => updateElement({ height: e.target.value as any } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="small">Pequena</option>
                <option value="medium">Média</option>
                <option value="large">Grande</option>
                <option value="extra">Extra</option>
              </select>
            </div>
            <div style={{ marginTop: '12px !important' }}>
              <div className="flex items-center justify-between h-6">
                <span className="text-sm font-medium text-slate-700 leading-none">Largura</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="px-2 py-0.5 text-slate-600 border border-slate-300 rounded"
                    onClick={() => updateElement({ widthPercent: Math.max(10, ((element as any).widthPercent ?? 100) - 5) })}
                  >
                    -
                  </button>
                  <span className="text-xs font-medium text-slate-600 leading-none">{((element as any).widthPercent ?? 100)}%</span>
                  <button
                    type="button"
                    className="px-2 py-0.5 text-slate-600 border border-slate-300 rounded"
                    onClick={() => updateElement({ widthPercent: Math.min(100, ((element as any).widthPercent ?? 100) + 5) })}
                  >
                    +
                  </button>
                </div>
              </div>
              <input
                type="range"
                min={10}
                max={100}
                step={1}
                value={(element as any).widthPercent ?? 100}
                onChange={e => updateElement({ widthPercent: Number(e.target.value) })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Animação</label>
              <select
                value={(element as any).animation || 'none'}
                onChange={e => updateElement({ animation: e.target.value as any })}
                className="mt-1 mb-3 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="none">Nenhuma</option>
                <option value="zoom">Zoom (hover/click)</option>
                <option value="pulse">Zoom contínuo + glow</option>
                <option value="bounce">Bounce contínuo</option>
                <option value="radar">Radar</option>
              </select>
            </div>
          </div>
        );

      case ElementType.Loading:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Título</label>
              <input
                type="text"
                value={(element as any).title || ''}
                onChange={e => updateElement({ title: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Analisando..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Subtítulos (animados)</label>
              <p className="mt-1 text-xs text-slate-500 mb-2">Adicione até 5 subtítulos que serão exibidos alternadamente durante o carregamento. Suporta HTML simples.</p>
              {(() => {
                const subtitles: string[] = (element as any).subtitles || [(element as any).subtitle || ''];
                return (
                  <div className="space-y-2">
                    {subtitles.map((sub: string, idx: number) => (
                      <div key={idx} className="flex gap-2">
                        <textarea
                          rows={2}
                          value={sub}
                          onChange={e => {
                            const newSubtitles = [...subtitles];
                            newSubtitles[idx] = e.target.value;
                            updateElement({ subtitles: newSubtitles, subtitle: newSubtitles[0] } as any);
                          }}
                          className="flex-1 px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                          placeholder={`Subtítulo ${idx + 1}...`}
                        />
                        {subtitles.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newSubtitles = subtitles.filter((_, i) => i !== idx);
                              updateElement({ subtitles: newSubtitles, subtitle: newSubtitles[0] } as any);
                            }}
                            className="p-2 text-red-500 hover:text-red-700"
                            title="Remover subtítulo"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    {subtitles.length < 5 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newSubtitles = [...subtitles, ''];
                          updateElement({ subtitles: newSubtitles } as any);
                        }}
                        className="w-full py-2 border-2 border-dashed border-slate-300 rounded-md text-slate-600 hover:border-slate-400 hover:text-slate-800 text-sm font-medium"
                      >
                        + Adicionar Subtítulo ({subtitles.length}/5)
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Tempo de carregamento (segundos)</label>
              <input
                type="number"
                min={0.1}
                step={0.1}
                value={(element as any).durationSeconds ?? 10}
                onChange={e => updateElement({ durationSeconds: Number(e.target.value) } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id={`showPercent-${element.id}`}
                type="checkbox"
                checked={(element as any).showPercent !== false}
                onChange={e => updateElement({ showPercent: e.target.checked } as any)}
              />
              <label htmlFor={`showPercent-${element.id}`} className="text-sm font-medium text-slate-700">Exibir percentual na barra</label>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Cor da barra</label>
              <input
                type="color"
                value={(typeof (element as any).barColor === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test((element as any).barColor)) ? (element as any).barColor : (config?.colors.primary || '#000000')}
                onChange={e => updateElement({ barColor: e.target.value } as any)}
                className="mt-1 block w-full h-10 p-1 border-slate-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Prosseguir automaticamente após (segundos)</label>
              <input
                type="number"
                min={0}
                step={0.5}
                value={typeof (element as any).autoProceedAfterSeconds === 'number' ? (element as any).autoProceedAfterSeconds : ''}
                onChange={e => {
                  const v = e.target.value;
                  const num = v === '' ? undefined : Number(v);
                  updateElement({ autoProceedAfterSeconds: num } as any);
                }}
                placeholder="Deixe vazio para não redirecionar"
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>
          </div>
        );

      case ElementType.Testimonial:
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Nome</label>
              <input
                type="text"
                value={(element as any).name || ''}
                onChange={e => updateElement({ name: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Digite o nome"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Info complementar</label>
              <input
                type="text"
                value={(element as any).info || ''}
                onChange={e => updateElement({ info: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="@usuario, cargo, cidade..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Estrelas (0–5)</label>
              <input
                type="number"
                min={0}
                max={5}
                value={typeof (element as any).stars === 'number' ? (element as any).stars : 5}
                onChange={e => updateElement({ stars: Math.max(0, Math.min(5, Number(e.target.value))) } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Texto</label>
              <textarea
                rows={3}
                value={(element as any).text || ''}
                onChange={e => updateElement({ text: e.target.value } as any)}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                placeholder="Digite o depoimento"
              />
            </div>
            <div>
              <ImageInput
                label="Imagem (URL)"
                value={(element as any).imageUrl || ''}
                onChange={val => updateElement({ imageUrl: val } as any)}
                onOpenLibrary={openImageLib}
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Cor das estrelas</label>
              <input
                type="color"
                value={(element as any).starColor || '#f59e0b'}
                onChange={e => updateElement({ starColor: e.target.value } as any)}
                className="mt-1 block w-full h-10 p-1 border border-slate-300 rounded-md"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Borda</label>
                <select
                  value={(element as any).border || 'small'}
                  onChange={e => updateElement({ border: e.target.value as any } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="none">Nenhuma</option>
                  <option value="small">Pequena</option>
                  <option value="medium">Média</option>
                  <option value="large">Grande</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Sombra</label>
                <select
                  value={(element as any).shadow || 'small'}
                  onChange={e => updateElement({ shadow: e.target.value as any } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                >
                  <option value="none">Nenhuma</option>
                  <option value="small">Pequena</option>
                  <option value="medium">Média</option>
                  <option value="large">Grande</option>
                </select>
              </div>
            </div>
          </div>
        );

      case ElementType.FbComments:
        const fbCommentsList = ((element as any).comments || []);
        const onDragEndFbComments = (event: any) => {
          const { active, over } = event;
          if (!over) return;
          const oldIndex = fbCommentsList.findIndex((c: any, i: number) => (c.id || String(i)) === active.id);
          const newIndex = fbCommentsList.findIndex((c: any, i: number) => (c.id || String(i)) === over.id);
          if (oldIndex < 0 || newIndex < 0) return;
          const reordered = [...fbCommentsList];
          const [moved] = reordered.splice(oldIndex, 1);
          reordered.splice(newIndex, 0, moved);
          updateElement({ comments: reordered } as any);
        };

        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Total de Comentários (para exibição)</label>
                <input
                  type="number"
                  value={(element as any).totalComments || 0}
                  onChange={e => updateElement({ totalComments: Number(e.target.value) } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
                <p className="text-xs text-slate-500 mt-1">Exibido no topo: "X of Y comments"</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Tempo de exibição de cada comentário (s)</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={(element as any).commentsDuration || 20}
                  onChange={e => updateElement({ commentsDuration: Number(e.target.value) } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">Texto "of" (ex: de)</label>
                <input
                  type="text"
                  value={(element as any).textOf || 'of'}
                  onChange={e => updateElement({ textOf: e.target.value } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Texto "comments" (ex: comentários)</label>
                <input
                  type="text"
                  value={(element as any).textComments || 'comments'}
                  onChange={e => updateElement({ textComments: e.target.value } as any)}
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="flex items-center">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={(element as any).showActions || false}
                  onChange={e => updateElement({ showActions: e.target.checked } as any)}
                  className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                Exibir ações (Curtir/Responder/Compartilhar)
              </label>
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Comentários</label>

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndFbComments}>
                <SortableContext items={fbCommentsList.map((c: any, i: number) => (c.id || String(i)))} strategy={verticalListSortingStrategy}>
                  {fbCommentsList.map((comment: any, index: number) => (
                    <FbCommentSortableItem
                      key={comment.id || String(index)}
                      id={comment.id || String(index)}
                      index={index}
                      comment={comment}
                      onUpdate={(idx, patch) => {
                        const newComments = [...fbCommentsList];
                        newComments[idx] = { ...newComments[idx], ...patch };
                        updateElement({ comments: newComments } as any);
                      }}
                      onDelete={(idx) => {
                        const newComments = [...fbCommentsList];
                        newComments.splice(idx, 1);
                        updateElement({ comments: newComments } as any);
                      }}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              <button
                onClick={() => {
                  const newComments = [...fbCommentsList];
                  newComments.push({
                    id: Math.random().toString(36).substr(2, 9),
                    name: 'Novo Usuário',
                    timeAgo: 'Agora',
                    content: 'Novo comentário...',
                    avatarUrl: ''
                  });
                  updateElement({ comments: newComments } as any);
                }}
                className="w-full py-2 border-2 border-dashed border-slate-300 rounded-md text-slate-600 hover:border-slate-400 hover:text-slate-800 text-sm font-medium"
              >
                + Adicionar Comentário
              </button>
            </div>
          </div>
        );



      default:
        return <div>Elemento desconhecido</div>;
    }
  };

  const getElementTypeName = (type: ElementType) => {
    switch (type) {
      case ElementType.Title: return 'Título';
      case ElementType.Text: return 'Texto';
      case ElementType.Alert: return 'Alerta';
      case ElementType.Timer: return 'Timer';
      case ElementType.Options: return 'Opções';
      case ElementType.List: return 'Lista';
      case ElementType.Price: return 'Preço';
      case ElementType.Level: return 'Nível';
      case ElementType.Image: return 'Imagem';
      case ElementType.BeforeAfter: return 'Antes/Depois';
      case ElementType.Audio: return 'Áudio';
      case ElementType.Video: return 'Vídeo';
      case ElementType.Button: return 'Botão';
      case ElementType.Spacer: return 'Espaçador';
      case ElementType.Loading: return 'Carregamento';
      case ElementType.Testimonial: return 'Depoimento';
      case ElementType.Carousel: return 'Carrossel';
      case ElementType.Seal: return 'Selo';
      case ElementType.FbComments: return 'Comentários FB';

      default: return 'Elemento';
    }
  };

  return (
    <>
      <h3 className="font-bold text-lg mb-4 text-slate-800">Propriedades do Componente</h3>
      <div className="border border-slate-200 rounded-lg p-4 mb-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-slate-800">{getElementTypeName(element.type)}</h4>
          <div className="flex items-center gap-1">
            {onMoveUp && (
              <button
                onClick={onMoveUp}
                className="p-1 text-slate-400 hover:text-slate-600"
                title="Mover para cima"
              >
                ↑
              </button>
            )}
            {onMoveDown && (
              <button
                onClick={onMoveDown}
                className="p-1 text-slate-400 hover:text-slate-600"
                title="Mover para baixo"
              >
                ↓
              </button>
            )}
            <button
              onClick={onDelete}
              className="p-1 text-red-500 hover:text-red-700"
              title="Remover elemento"
              aria-label="Remover elemento"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                <path d="M3 6h18" />
                <path d="M8 6V4h8v2" />
                <path d="M6 6l1 14h10l1-14" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
              </svg>
            </button>
          </div>
        </div>
        {element.type === ElementType.Options ? (
          <div className="mb-3">
            <label className="block text-sm font-medium text-slate-700">ID</label>
            <input
              type="text"
              value={(element as any).reportId || ''}
              onChange={e => onUpdate({ ...element, reportId: e.target.value } as any)}
              className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              placeholder="Identificador único da pergunta"
            />
          </div>
        ) : null}
        {renderElementContent()}
        {element.type === ElementType.Button && (
          <div className="mt-3">
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700">Ação do Botão</label>
              <select
                value={(element as any).action || 'next'}
                onChange={e => {
                  updateElement({ action: e.target.value as any } as any);
                  if (e.target.value === 'redirect') {
                    setTimeout(() => redirectUrlRef.current?.focus(), 100);
                  }
                }}
                className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              >
                <option value="next">Avançar</option>
                <option value="redirect">Redirecionar</option>
              </select>
            </div>
            {(((element as any).action || 'next') === 'redirect') && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Destino do redirecionamento (URL)</label>
                  <input
                    ref={redirectUrlRef}
                    type="url"
                    value={(element as any).redirectUrl || ''}
                    onChange={e => updateElement({ redirectUrl: e.target.value } as any)}
                    className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    placeholder="https://exemplo.com"
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-slate-700">Onde abrir</label>
                  <select
                    value={(element as any).target || '_blank'}
                    onChange={e => updateElement({ target: e.target.value as any } as any)}
                    className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  >
                    <option value="_self">Mesma aba</option>
                    <option value="_blank">Nova aba</option>
                  </select>
                </div>
              </>
            )}
          </div>
        )}
        <div className="mt-3">
          <label className="block text-sm font-medium text-slate-700">Mostrar após (segundo)</label>
          <input
            type="number"
            min={0}
            step={1}
            value={typeof (element as any).showAfterSeconds === 'number' ? (element as any).showAfterSeconds : 0}
            onChange={e => onUpdate({ ...element, showAfterSeconds: Math.max(0, parseInt(e.target.value || '0', 10) || 0) } as any)}
            className="mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            placeholder="0"
          />
        </div>
      </div>
      {imgLib.isOpen && (
        <ImageLibrary
          onSelect={(url) => { imgLib.callback?.(url); setImgLib({ isOpen: false }); }}
          onClose={() => setImgLib({ isOpen: false })}
        />
      )}
    </>
  );
};

export default ElementEditor;
